import type { FloorPhotoSearchCandidate } from "./shopMode";

export type Role =
  | "OWNER"
  | "ADMIN"
  | "MANAGER"
  | "FLORIST"
  | "COURIER"
  | "HOUSEKEEPING";

/**
 * Roles that hold no shop assignment — mirrors the server's
 * `ROLES_WITHOUT_SHOP` (src/common/constants/domain-enums.ts). The server
 * refuses an assignment for these; the UI simply does not offer one.
 */
export const SHOP_FREE_ROLES: ReadonlySet<Role> = new Set<Role>([
  "COURIER",
  "HOUSEKEEPING",
]);
export type ZoneType = "FRIDGE" | "DRY_STORAGE" | "FRONT_SHOP" | "CUSTOM" | "BOX";

export type VehicleType = "BIKE" | "MOTORBIKE" | "CAR" | "VAN";

export interface CourierProfileSummary {
  vehicleType: VehicleType;
  vehiclePlate: string | null;
  isAvailable: boolean;
  notes: string | null;
}
export type DefaultUom = "STEM" | "UNIT" | "CM" | "ROLL";

export interface AssignedLocationRef {
  id: string;
  name: string;
  code: string;
}

export interface SessionUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName: string;
  phoneNumber?: string | null;
  jobTitle?: string | null;
  role: Role;
  assignedLocation?: AssignedLocationRef | null;
  /** OD-79 / D-90: the server's decision whether this person sees pay (owner/admin). Absent = no. */
  payVisible?: boolean;
  /** G-136: their sign-in address is confirmed. Absent = not known, so nothing is shown. */
  emailVerified?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  expiresInSeconds: number;
  sessionMaxAgeSeconds: number;
  user: SessionUser;
}

export interface RefreshResponse {
  accessToken: string;
  expiresInSeconds: number;
  sessionMaxAgeSeconds: number;
}

/**
 * G-85 S6 — the server's spelling of a language (the `AppLanguage` enum).
 * UPPERCASE because it is a Prisma enum; `web/src/i18n/languages.ts` owns the
 * lowercase BCP-47-ish codes the UI uses, and converts between the two.
 * `null` means the person has never chosen — NOT that they chose English.
 */
export type AppLanguage = "EN" | "ES" | "CA" | "RU" | "UK";

export interface MeResponse {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName: string;
  phoneNumber?: string | null;
  jobTitle?: string | null;
  role: Role;
  isActive: boolean;
  lastLoginAt: string | null;
  assignedLocation?: AssignedLocationRef | null;
  /** OD-79 / D-90: the server's decision whether this person sees pay (owner/admin). */
  payVisible?: boolean;
  workContractConfigured?: boolean;
  workContractOriginalName?: string | null;
  workContractUploadedAt?: string | null;
  shopPinConfigured?: boolean;
  /** G-85 S6: null = never chosen, so the kiosk follows the tablet. */
  language?: AppLanguage | null;
  /** G-136: the person proved they read mail at their current address. */
  emailVerified?: boolean;
  /** G-136: a new sign-in address waiting for its confirmation link. */
  pendingEmail?: string | null;
}

/**
 * G-136 — where a person's account stands (the server's call):
 * ACTIVE has a password; INVITE_SENDING the invitation is queued; INVITED the
 * mail server accepted it (never "delivered"); INVITE_FAILED it could not be
 * sent; INVITE_EXPIRED the link ran out; SETUP_PENDING no live invitation.
 */
export type AccountStatus =
  | "ACTIVE"
  | "INVITE_SENDING"
  | "INVITED"
  | "INVITE_FAILED"
  | "INVITE_EXPIRED"
  | "SETUP_PENDING";

/** G-136: what an emailed account link is for. */
export type AccountLinkPurpose = "INVITE" | "PASSWORD_RESET" | "EMAIL_VERIFY" | "EMAIL_CHANGE";

/** G-136: what the person who opened a link is shown before acting on it. */
export interface AccountLinkPreview {
  purpose: AccountLinkPurpose;
  /** Whose account the link is for. */
  userId: string;
  /** The address the link is for — the new one, for a change. */
  email: string;
  firstName: string | null;
  language: AppLanguage | null;
  expiresAt: string;
}

export type ShopifyLinkType = "ITEM" | "BOUQUET_TEMPLATE";
export type ShopifyOrderStatus =
  | "NEW"
  | "OFFERED"
  | "PREPARING"
  | "FULFILLED"
  | "MANAGER_REVIEW"
  | "UNFULFILLABLE"
  | "CANCELLED";

export interface ShopifyCredentialStatus {
  connected: boolean;
  storeDomain: string | null;
  apiVersion: string | null;
  clientId: string | null;
  scopes: string[];
  blogScopeMissing: boolean;
  lastOrderSyncAt: string | null;
  lastProductSyncAt: string | null;
}

/** G-122 — the Telegram page. */
export interface TelegramStatus {
  botConfigured: boolean;
  botUsername: string | null;
  botProblem: string | null;
  group: {
    configured: boolean;
    reachable: boolean;
    title: string | null;
    topicsEnabled: boolean | null;
    canManageTopics: boolean | null;
    problem: string | null;
  };
  lastDailyReport: {
    windowEnd: string;
    orderCount: number;
    totalEur: string;
    sentCount: number;
    failedCount: number;
    staleSince: string | null;
  } | null;
}

/** G-122 S2 — one person on the Telegram page. */
export interface TelegramPersonRow {
  userId: string;
  fullName: string;
  role: Role;
  isActive: boolean;
  telegramUserId: string | null;
  receives: "DAILY_TOTAL" | "NOTHING_YET" | "NOTHING_INACTIVE";
}

/** G-122 S2 — one account the dropdowns offer (the staff bot's roster). */
export interface TelegramAccountOption {
  telegramUserId: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  shop: string | null;
  fromBot: boolean;
  isBotOwner: boolean;
  inGroup: boolean | null;
}

export interface TelegramPeople {
  rows: TelegramPersonRow[];
  options: TelegramAccountOption[];
  rosterReceivedAt: string | null;
}

export interface TelegramTestResult {
  ok: boolean;
  problem: string | null;
}

export interface SetShopifyCredentialPayload {
  storeDomain: string;
  apiVersion?: string;
  clientId: string;
  clientSecret: string;
}

/** One size of a Shopify product, from the CRM's stored copy of the catalogue. */
export interface ShopifyVariant {
  shopifyVariantId: string;
  shopifyProductId: string;
  productTitle: string;
  variantTitle: string | null;
  sku: string | null;
  barcode: string | null;
  priceEur: string;
  imageUrl: string | null;
  status: string;
  removed: boolean;
  /** Open orders held back because this size is not linked yet. */
  waitingOrders: number;
  linkedTo: {
    linkId: string;
    type: ShopifyLinkType;
    id: string;
    name: string;
    imageKey: string | null;
  } | null;
}

export interface ShopifyMatchItem {
  id: string;
  name: string;
  barcode: string | null;
  sku: string | null;
  imageKey: string | null;
}

/** G-60 item 1: proposed item ↔ variant pairs, and what matched nothing. */
export interface ShopifyBarcodeMatches {
  proposals: Array<{
    variant: ShopifyVariant;
    matchedBy: "barcode" | "sku";
    candidates: ShopifyMatchItem[];
  }>;
  unmatchedVariants: ShopifyVariant[];
  unmatchedItems: ShopifyMatchItem[];
}

export type ShopifyLinkFilter = "all" | "linked" | "unlinked";

export interface ShopifyCatalogRefreshResult {
  refreshedAt: string;
  products: number;
  variants: number;
  removed: number;
  truncatedProducts: string[];
}


export interface ShopifyLinkResult {
  id: string;
  shopifyVariantId: string;
  linkType: ShopifyLinkType;
  itemId: string | null;
  bouquetTemplateId: string | null;
  /** Open orders re-checked because of this link. */
  reevaluatedOrders: number;
}

export interface CreateShopifyLinkPayload {
  shopifyVariantId: string;
  linkType: ShopifyLinkType;
  itemId?: string;
  bouquetTemplateId?: string;
}

/** G-113 S2: one piece to bring from another shop; `itemId` prefills the transfer form. */
export interface ShopifyTransferSuggestion {
  description: string;
  qty: string;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
  itemId: string | null;
  /** G-113 S3: bring a roll of paper/ribbon — `qty` is not a length. */
  packaging?: { categoryCode: "PAPER" | "RIBBON"; colorNames: string[] };
}

/** Stock picture of an OPEN order; null for claimed, handed-over or cancelled ones. */
export interface ShopifyFeasibilitySummary {
  candidates: string[];
  shortfall: Array<{
    description: string;
    requiredQty: string;
    availableQty: string;
    /** A Shopify product nobody linked yet, not a stock count. */
    unlinked?: boolean;
    /** A paper/ribbon roll: present or not, never a count. */
    presenceOnly?: boolean;
    /** The line as a person reads it — the server's one wording. */
    text: string;
  }>;
}

export interface ShopifyOrderLineView {
  id: string;
  kind: "ITEM" | "BOUQUET" | "UNLINKED";
  titleRaw: string;
  shopifyVariantId: string | null;
  quantity: string;
  unitPriceEur: string;
  lineTotalEur: string;
  itemId: string | null;
  itemName?: string | null;
  bouquetTemplateId: string | null;
  bouquetTemplateName?: string | null;
  bouquetTemplate?: BouquetTemplate | null;
  bouquetBuildId: string | null;
}

export interface ShopifyOrderView {
  id: string;
  shopifyOrderId: string;
  orderNumber: string | null;
  status: ShopifyOrderStatus;
  fulfillmentMethod?: "PICK_UP" | "DELIVERY" | "REVIEW" | null;
  pickupRoutingIssue?: string | null;
  totalEur: string;
  /** KI-150: goods, shipping, tax, discount, payment state. */
  money: ShopifyMoneyFacts;
  customerName: string | null;
  claimedLocationId: string | null;
  claimedAt: string | null;
  fulfilledAt: string | null;
  saleId: string | null;
  /** OD-104: what Shopify changed after a shop accepted; null once acknowledged. */
  shopifyChange: { changes: string[]; detectedAt: string } | null;
  shopifyChangedAt: string | null;
  /** OD-102: set when no single shop holds it all — offered to this shop only. */
  offeredToLocationId: string | null;
  offeredToLocation: { id: string; name: string; code: string } | null;
  /** The pieces the offered shop must bring in; null once it holds everything. */
  transferPlan: ShopifyTransferSuggestion[] | null;
  feasibility: ShopifyFeasibilitySummary | null;
  lines: ShopifyOrderLineView[];
  receivedAt: string;
  requestedFor: string | null;
  /**
   * G-167 (OD-283): the delivery day in Barcelona days and how far off it is
   * (the line is today or tomorrow), its preparation day (the day before)
   * and the slot as the card shows it ("16:00–20:00"). Null: undated, which
   * counts as today.
   */
  due: {
    day: string;
    when: WebOrderDueWhen;
    prepDay: string;
    window: string | null;
  } | null;
  /** G-167: waiting in the office's "To assign" list. */
  toAssign: boolean;
  /** G-167: the office gave it to its shop from "To assign". */
  assigned: { at: string; by: { id: string; fullName: string } | null } | null;
  /** G-167: the last "Pass to another shop". */
  lastPass: {
    at: string;
    from: { id: string; name: string };
    by: { id: string; fullName: string };
    reason: string;
  } | null;
  /** G-167: what no shop holds for an office-placed order, from its preparation day. */
  prepShortfall: ShopifyFeasibilitySummary["shortfall"] | null;
  /** Shopify's shipping address as Shopify sent it (street, zip, city…). */
  shippingAddress?: { address1?: string | null; zip?: string | null; city?: string | null } | null;
  claimedLocation: { id: string; name: string; code: string } | null;
  claimedBy: { id: string; fullName: string } | null;
  /** G-160: the order in Shopify admin; null when no store is connected. */
  adminUrl?: string | null;
  /** G-160 (OD-264): null while nothing in it is linked — owners only see these. */
  surfacedAt: string | null;
  /** G-160 (OD-266): when the current offer began; "waiting" counts from here. */
  offeredAt: string | null;
  /** G-160 (OD-263): why it needs someone, most urgent first; [] when nobody. */
  attention: WebOrderAttentionReason[];
  /** G-160 (OD-265): set while an owner has it skipped. */
  skipped: {
    /** G-176 (KI-90): a Shopify test order, skipped by the poll; no undo. */
    test: boolean;
    at: string;
    by: { id: string; fullName: string } | null;
    reason: ShopifyOrderSkipReason;
    note: string | null;
  } | null;
  /** The courier job of its calendar copy, once handed over. */
  delivery: {
    id: string;
    status: string;
    failureReason: string | null;
    courierName: string | null;
  } | null;
}

/** G-167: how far off a web order's delivery day is (server: web-order-dates.ts). */
export type WebOrderDueWhen = "PAST" | "TODAY" | "TOMORROW" | "LATER";

/** G-167: how a shop stands for an order on today's stock (assign / pass pickers). */
export interface ShopifyShopFit {
  locationId: string;
  name: string;
  /** The shop that has the order now. */
  current: boolean;
  fit: "ALL" | "TRANSFER" | "SHORT";
  /** The pieces to bring, or what no shop holds — the server's wording. */
  missing: string[];
}

/** G-167: an order the office passed away from this shop today (the tablet's notice). */
export interface ShopifyPassedAway {
  orderId: string;
  orderNumber: string;
  toLocationName: string;
  reason: string;
  passedAt: string;
}

/** G-160 (OD-263): the reasons a web order needs someone (server: web-order-attention.ts). */
export type WebOrderAttentionReason =
  | "CANT_BE_MADE"
  | "NOT_LINKED"
  | "NOTHING_LINKED"
  | "DELIVERY_FAILED"
  | "OVERDUE_NOT_BUILT"
  | "STOCK_TO_MOVE"
  | "CHANGED_IN_SHOPIFY"
  | "MULTI_BOUQUET_LINE"
  | "DUE_TODAY_NOT_BUILT"
  | "DUE_TOMORROW_NOT_BUILT"
  | "WAITING";

/** G-160 (OD-265): why an owner skipped a web order. */
export type ShopifyOrderSkipReason =
  | "MADE_OUTSIDE_CRM"
  | "CANCELLED_WITH_CUSTOMER"
  | "DUPLICATE"
  | "TEST"
  | "OTHER";

/** G-160: the menu's counts — orders by their most urgent tone, each once. */
export interface WebOrderAttentionCounts {
  blocked: number;
  soon: number;
  info: number;
  unlinkedProducts: number;
  /** The Web orders badge: the "To handle" orders plus those waiting in "To assign", each once. */
  toHandle: number;
  /** G-167: orders due later than tomorrow waiting for the office to pick a shop. */
  toAssign: number;
}

/**
 * G-143 — web orders by shop. Money is a two-decimal string. `costEur` is
 * present only for owners: the server leaves the key out of a manager's
 * answer (OD-220), so a page shows cost exactly when the key is there.
 */
export interface WebOrdersMoney {
  /** Shopify's total less what Shopify refunded (G-166). */
  customerPaidEur: string;
  /** The hand-over sales less their refunds (G-166). */
  bookedEur: string;
  /** G-166: goods refunds already taken off `bookedEur`. */
  refundedEur: string;
  bouquetsEur: string;
  itemsEur: string;
  vatEur: string;
  costEur?: string;
  profitEur: string;
  commissionEur: string;
  /** OD-198: sale lines whose cost is unknown; profit is then overstated. */
  unknownCostLines: number;
}

export interface WebOrdersPerson {
  id: string;
  fullName: string;
}

export interface WebOrdersShop {
  id: string;
  name: string;
  code: string;
}

export interface WebOrdersCounts {
  received: number;
  delivered: number;
  inProgress: number;
  deliveryFailed: number;
  cancelled: number;
  notMade: number;
  waiting: number;
  handedOver: number;
  /** G-160 (OD-265): skipped by an owner, and by reason. */
  skipped: number;
  skippedByReason: Partial<Record<ShopifyOrderSkipReason, number>>;
  /** Neither handed over, skipped nor cancelled. */
  stillOpen: number;
}

export interface WebOrdersPeriod {
  from: string;
  to: string;
  locationId: string | null;
}

export interface WebOrdersReport {
  period: WebOrdersPeriod;
  showsCost: boolean;
  totals: WebOrdersCounts & { money: WebOrdersMoney };
  shops: (WebOrdersCounts & {
    shop: WebOrdersShop;
    averageMinutesToAccept: number | null;
    money: WebOrdersMoney;
  })[];
  notTaken: WebOrdersCounts;
  people: {
    person: WebOrdersPerson;
    shopNames: string[];
    accepted: number;
    bouquetsBuilt: number;
    handedOver: number;
    money: WebOrdersMoney;
  }[];
  couriers: {
    courier: WebOrdersPerson;
    delivered: number;
    outNow: number;
    failed: number;
    averageMinutesPickupToDoor: number | null;
    payEur: string;
  }[];
}

export type WebOrderState =
  | "OFFERED"
  | "PREPARING"
  | "NEEDS_REVIEW"
  | "HANDED_OVER"
  | "WITH_COURIER"
  | "DELIVERED"
  | "DELIVERY_FAILED"
  | "CANCELLED"
  | "NOT_MADE"
  | "SKIPPED";

export interface WebOrderRow {
  id: string;
  orderNumber: string | null;
  arrivedAt: string;
  state: WebOrderState;
  shop: WebOrdersShop | null;
  /** Shopify admin link; null when no store is connected. */
  adminUrl: string | null;
  /** Shopify's total less what Shopify refunded (G-166). */
  customerPaidEur: string;
  /** G-166: what Shopify gave back on the order. */
  refundedInShopifyEur: string;
  acceptedBy: WebOrdersPerson | null;
  builtBy: WebOrdersPerson[];
  handedOverBy: WebOrdersPerson | null;
  courier: WebOrdersPerson | null;
  deliveryJobId: string | null;
  lines: { kind: string; title: string; quantity: string }[];
}

export type WebOrderPart = "ACCEPTED" | "BUILT" | "HANDED_OVER" | "COURIER";

export interface WebOrdersPersonReport {
  period: WebOrdersPeriod;
  showsCost: boolean;
  person: WebOrdersPerson & { role: string };
  totals: {
    orders: number;
    accepted: number;
    bouquetsBuilt: number;
    handedOver: number;
    delivered: number;
    money: WebOrdersMoney | null;
  };
  orders: (WebOrderRow & {
    parts: WebOrderPart[];
    /** The order's money when this person handed it over; else null. */
    money: WebOrdersMoney | null;
    bookedTo: WebOrdersPerson | null;
  })[];
}

export type WebOrderStepKind =
  | "RECEIVED"
  | "OFFERED"
  | "ACCEPTED"
  | "BUILT"
  | "HANDED_OVER"
  | "COURIER_ASSIGNED"
  /** G-167: the office gave it to a shop / passed it to another. */
  | "ASSIGNED"
  | "PASSED"
  | "PICKED_UP"
  | "DELIVERED"
  | "DELIVERY_FAILED"
  | "CANCELLED";

export interface WebOrderJourney extends WebOrderRow {
  showsCost: boolean;
  steps: { at: string; kind: WebOrderStepKind; person: WebOrdersPerson | null; detail: string | null }[];
  bouquets: {
    title: string;
    builtBy: WebOrdersPerson;
    builtAt: string;
    /** `costEur`: owners only; null = unknown (OD-198, a part of it has no known cost). */
    stems: { name: string; quantity: string; costEur?: string | null }[];
  }[];
  money: WebOrdersMoney | null;
  courierPayEur: string | null;
  hasProofPhoto: boolean;
  /** Only on a "could not be made" order: why, checked against stock now. */
  cannotBeMade: { pickupIssue: string | null; missing: string[] } | null;
}

export interface WebOrdersReportQuery {
  locationId?: string;
  from?: string;
  to?: string;
}

export interface Zone {
  id: string;
  name: string;
  zoneType: ZoneType;
  isActive: boolean;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  code: string;
  isActive: boolean;
  shopifyStoreDomain?: string | null;
  shopifyLocationId?: string | null;
  /** D-6 shop pin (courier geo-verification target); null until geocoded or set manually. */
  lat: string | null;
  lng: string | null;
  /** G-203: how to reach the shop; null when not set. */
  phone: string | null;
  publicEmail: string | null;
  /** G-203: printed on the courtesy ticket's NIF block when set. */
  nif: string | null;
  /** G-203: the shop's own Google Maps link, or null. */
  mapsUrl: string | null;
  /**
   * G-203: the weekly pattern. One entry per opening range; a weekday with
   * two is a split day; a weekday with none is closed. No entries at all means
   * the hours were never set.
   */
  openingHours: OpeningHoursRange[];
  /** G-203: dates the shop does not open, ascending, past ones included. */
  closedDates: ClosedDate[];
  zones: Zone[];
}

export interface OpeningHoursRange {
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
  /** "09:00" */
  opensAt: string;
  /** "20:00" */
  closesAt: string;
}

export interface ClosedDate {
  /** "2026-12-25" */
  date: string;
  note: string | null;
}

/** What the store offers as pickup locations and which shop each is mapped to. */
export interface ShopifyPickupOptions {
  connected: boolean;
  storeDomain: string | null;
  locations: Array<{ id: string; name: string; address: string; mappedLocation: { id: string; name: string } | null }>;
}

export interface UpdateLocationPayload {
  isActive?: boolean;
  address?: string;
  lat?: number;
  lng?: number;
  /** A blank string or null clears the field (G-203). */
  phone?: string | null;
  publicEmail?: string | null;
  nif?: string | null;
  /** An https link; blank or null clears it (G-203). */
  mapsUrl?: string | null;
  /** Replaces the whole weekly pattern when present (G-203). */
  openingHours?: OpeningHoursRange[];
  /** Replaces the whole closed-date list when present (G-203). */
  closedDates?: Array<{ date: string; note?: string }>;
}

/**
 * G-64 — a shop's stored receipt design as the API returns it.
 *
 * `blocks` is `null` when no row exists, which is a real state meaning "use the
 * default template" — not an error and not an empty ticket.
 */
export interface StoredReceiptTemplate {
  locationId: string;
  blocks: unknown[] | null;
  logoKey: string | null;
  logoScale: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface ItemCategory {
  id: string;
  code: string;
  label: string;
  /**
   * G-30: Spanish IVA percent for every item in this category, as a decimal
   * string (e.g. "10", "21"). Present on both planes — the floor taxonomy
   * endpoint reuses the same service read, and a VAT rate is not a secret.
   */
  vatRatePercent: string;
  /**
   * G-45: the target margin the pricing guidance aims at, as a decimal string
   * (e.g. "60"). **Percent of the NET (ex-VAT) price, not a markup on cost** —
   * 60 turns a €0.62 cost into a €1.55 net price, not €0.99.
   */
  targetMarginPercent: string;
}

/**
 * G-45: the floor and the target for one thing being priced. Every figure is
 * computed server-side (`common/pricing/price-floor.ts`) so no screen re-derives
 * the arithmetic and none of them can disagree.
 */
export interface PriceGuidance {
  cogsEur: string;
  vatRatePercent: string;
  /** Never sell below this: net revenue would not cover cost. */
  minPriceEur: string;
  /** The price that leaves the category's target margin. */
  targetPriceEur: string;
}

export interface Color {
  id: string;
  name: string;
  /** Display swatch, e.g. "#E53935". Null for non-visual colors like "Mixed". */
  hex: string | null;
}

/** Controlled flower-type taxonomy (Roses, Peonies…) nested under a category. */
export interface SubCategory {
  id: string;
  code: string;
  label: string;
  /** Present on the catalog list endpoint; omitted by the floor feed. */
  categoryId?: string;
}


/**
 * G-121 S3 (OD-138): which side of IVA a typed purchase price is on. There is
 * no default — the person picks one before saving. The server converts with
 * the item category's rate; the browser never does IVA arithmetic.
 */
export type TypedMoneyBasis = "BEFORE_IVA" | "INCLUDES_IVA";

/**
 * G-121 S3: how a purchase's money was given and where its IVA lives —
 * typed on one side of IVA (rate, IVA and gross kept on the row), or
 * INVOICE (read off a supplier invoice; the IVA is on the invoice). Null =
 * unknown history, or no money given.
 */
export type PurchaseMoneyBasis = TypedMoneyBasis | "INVOICE";

/** G-121 S3: the money facts a purchase batch keeps beside its net total. */
export interface PurchaseMoneyFacts {
  moneyBasis: PurchaseMoneyBasis | null;
  ivaRatePercent: string | null;
  ivaEur: string | null;
  grossTotalEur: string | null;
}

/** What a receive answers with: the batch's net money and its facts. */
export interface ReceivedBatchMoney extends PurchaseMoneyFacts {
  id: string;
  /** Before IVA. */
  invoiceTotalEur: string;
  /** WO-B part 4: null when nobody knows what the stock cost — never €0. */
  purchaseUnitCostEur: string | null;
}

export interface CatalogItem {
  id: string;
  name: string;
  /** G-66: grower / brand the supplier named. Provenance, never a category. */
  producer?: string | null;
  /** G-73: free text about the product that no other column carries. */
  description?: string | null;
  defaultUom: DefaultUom;
  isPerishable: boolean;
  isActive: boolean;
  sku?: string | null;
  barcode?: string | null;
  defaultPurchaseUnitCostEur?: string | null;
  defaultSaleUnitPriceEur?: string | null;
  subCategoryId?: string | null;
  subCategoryRef?: SubCategory | null;
  reorderThreshold?: string | null;
  imageKey?: string | null;
  /** Bucket / vase — cm; from API as decimal string */
  diameterCm?: string | null;
  depthCm?: string | null;
  /**
   * `vatRatePercent` (G-121 S3): the category's IVA rate, which the purchase
   * cost switch names ("Includes IVA 21 %"). Every CRM item read carries it.
   */
  category: { id: string; code: string; label: string; vatRatePercent: string };
  /** Single color (e.g. a flower's). Null on groups and non-colored items. */
  colorId?: string | null;
  color?: { id: string; name: string; hex: string | null } | null;
  /**
   * Only on the single-item read (`GET /items/:id`): what an EMPTY price box
   * means. The price the sale screens use when nobody typed one (cost ×
   * category margin + VAT), and the cost of the last real delivery. `null` =
   * nothing to work it out from yet.
   */
  derivedSaleUnitPriceEur?: string | null;
  lastDeliveryUnitCostEur?: string | null;
  /** Latest supplier across all shops; null when absent or inactive. Single-item read only. */
  lastSupplierId?: string | null;
}

/** A row of the list read (`GET /items`), which the single-item read is not. */
export interface CatalogListItem extends CatalogItem {
  /**
   * Linked to at least one Shopify product that is for sale — still in the
   * store and active (OD-119). A link to a deleted, draft or archived
   * product does not count: that item needs a product again.
   */
  shopifyLinked: boolean;
}

export interface UpdateItemPayload {
  name?: string;
  producer?: string | null;
  /** G-73: free text about the product that no other column carries. */
  description?: string | null;
  categoryId?: string;
  sku?: string | null;
  barcode?: string | null;
  defaultPurchaseUnitCostEur?: number | null;
  /** G-121 S3: required with a cost — the side of IVA it was typed on. */
  defaultPurchaseUnitCostBasis?: TypedMoneyBasis;
  defaultSaleUnitPriceEur?: number | null;
  subCategoryId?: string | null;
  reorderThreshold?: number | null;
  diameterCm?: number | null;
  depthCm?: number | null;
  isActive?: boolean;
  /** Set a color (UUID) or null to detach. */
  colorId?: string | null;
  defaultUom?: DefaultUom;
}

export interface InventoryBalance {
  id: string;
  qtyOnHand: string;
  /** Weighted avg unit cost from active batches; only for non-perishable, non-FLOWER items. */
  /**
   * G-75: what the shop PAID. Optional because the kiosk strips it for a
   * florist — cost is a manager's fact, the same rule the item card uses. The
   * CRM balance read always carries it.
   */
  avgPurchaseUnitCostEur?: string | null;
  /**
   * WO-B part 4 (OD-198): the lots' units the average speaks for, and the
   * units whose cost nobody knows (left out of the average). A value is the
   * average × the units certainly of known cost (`knownCostQtyOf`), never ×
   * the whole row. Null where the average does not apply; absent for a
   * florist on the kiosk.
   */
  knownCostQty?: string | null;
  unknownCostQty?: string | null;
  /**
   * G-75: what the CUSTOMER pays, resolved through the G-45 effective price
   * (a typed price wins; otherwise derived from real batch cost). Present on
   * the kiosk read only. `null` means it genuinely cannot be worked out —
   * show "price unknown", never €0.00.
   */
  saleUnitPriceEur?: string | null;
  saleUnitPriceIsDerived?: boolean;
  /**
   * G-84: set when this row's zone IS a storage box — the box's code, its
   * optional name and the zone it is kept in. Present on the kiosk read only,
   * so a florist reading "3 in BOX-000017" also learns where to go looking.
   */
  box?: { code: string; name: string | null; keptInZoneName: string | null } | null;
  /** ISO 8601: last time stock entered this zone (receives + internal transfers). */
  lastStockInAt: string | null;
  item: {
    id: string;
    name: string;
    /**
     * G-83: what a scan matches against. Null for anything never scanned at
     * receiving, which is most of the catalog. NOT unique — one code can name
     * several items (G-61), so a scan legitimately matches more than one row.
     */
    barcode: string | null;
    defaultUom: DefaultUom;
    isPerishable: boolean;
    imageKey: string | null;
    diameterCm: string | null;
    depthCm: string | null;
    category: { id: string; code: string; label: string };
    color: { id: string; name: string; hex: string | null } | null;
    subCategoryRef: { id: string; code: string; label: string } | null;
  };
  location: { id: string; name: string; code: string };
  zone: { id: string; name: string; zoneType: ZoneType };
}

export interface CreateItemPayload {
  name: string;
  /** G-119: the ids of the active items the person was shown as twins (from the 409 DUPLICATE_NAME) and creates beside anyway. */
  confirmDuplicateOf?: string[];
  producer?: string;
  /** G-73: free text about the product that no other column carries. */
  description?: string;
  categoryId: string;
  defaultUom: DefaultUom;
  sku?: string;
  barcode?: string;
  defaultPurchaseUnitCostEur?: number;
  /** G-121 S3: required with a cost — the side of IVA it was typed on. */
  defaultPurchaseUnitCostBasis?: TypedMoneyBasis;
  defaultSaleUnitPriceEur?: number;
  isPerishable?: boolean;
  subCategoryId?: string;
  reorderThreshold?: number;
  diameterCm?: number;
  depthCm?: number;
  /** Single color (UUID). */
  colorId?: string;
}

export interface CreateItemCategoryPayload {
  label: string;
  /** G-30: required — a category can never exist without a VAT rate. */
  vatRatePercent: number;
  /**
   * G-45: required for the same reason — without a target margin the category
   * would price at cost and call it a good price. Percent of the NET price;
   * must be under 100.
   */
  targetMarginPercent: number;
}

export interface ReceiveStockPayload {
  itemId: string;
  locationId: string;
  zoneId: string;
  quantity: number;
  purchasedAt: string;
  receivedAt: string;
  /**
   * G-91: OPTIONAL, matching the server's own DTO since G-86. Omit it when
   * nobody knew the price: the service seeds the unit cost from the item's last
   * real batch or the catalogue default and books zero spend, which is an
   * honest unknown. Sending 0 instead would assert the goods were free.
   */
  invoiceTotalEur?: number;
  /** G-121 S3: required with `invoiceTotalEur`. */
  moneyBasis?: TypedMoneyBasis;
  invoiceNumber?: string;
}

export interface TransferStockPayload {
  itemId: string;
  locationId: string;
  fromZoneId: string;
  toZoneId: string;
  quantity: number;
  reason: string;
}

export interface AdjustStockPayload {
  itemId: string;
  locationId: string;
  zoneId: string;
  targetQtyOnHand: number;
  reason: string;
  targetDiameterCm?: number;
  targetDepthCm?: number;
}

export interface AdjustStockResponse {
  ok: boolean;
  changed: boolean;
  qtyOnHand: string;
  batchId?: string;
}

export interface TransferBetweenLocationsPayload {
  itemId: string;
  fromLocationId: string;
  fromZoneId: string;
  toLocationId: string;
  toZoneId: string;
  quantity: number;
  reason: string;
}

/** G-136: no password — the person is invited at `email` and chooses their own. */
export interface CreateUserPayload {
  email: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  role: Role;
  assignedLocationId?: string;
  /** G-103 S2 (OD-84): 1–31; omitted = the 1st. */
  payDayOfMonth?: number;
  /** G-103 S2 (OD-82): fixed monthly salary; omitted = hourly. */
  monthlySalaryEur?: number;
  /** G-145: hourly rate, set with the person. Never together with a salary. */
  hourlyRateEur?: number;
  /** G-145: sales commission percent, 0–100. */
  salesCommissionPercent?: number;
  /** G-145: the person's language — the invitation is sent in it. Omitted = not chosen. */
  language?: AppLanguage;
  /** COURIER only — required for that role. */
  vehicleType?: VehicleType;
  /** COURIER only. */
  vehiclePlate?: string;
  /** COURIER only. */
  courierNotes?: string;
  /** COURIER only. OD-5 vehicle carry cap; omit for no limit. */
  maxConcurrentJobs?: number;
}

export interface UserAssignmentSummary {
  id: string;
  startedAt: string;
  notes: string | null;
  location: { id: string; name: string; code: string };
  assignedBy: { id: string; fullName: string };
}

export interface UserAssignmentRecord extends UserAssignmentSummary {
  endedAt: string | null;
  createdAt: string;
}

export interface UserListEntry {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName: string;
  phoneNumber?: string | null;
  jobTitle?: string | null;
  role: Role;
  isActive: boolean;
  createdAt: string;
  /** EUR/hour for payroll; from API as decimal string or null */
  hourlyRateEur?: string | null;
  /** 0–100, % of sale total for sales they logged; decimal string or null */
  salesCommissionPercent?: string | null;
  /** G-103 S2 (OD-82): fixed monthly salary; owner-only like the rate. null = hourly. G-163: the one in force this month. */
  monthlySalaryEur?: string | null;
  /** G-163 (owner 2026-10-19): a salary change saved to start in a later month ("YYYY-MM"); owner-only. */
  upcomingSalary?: { monthlySalaryEur: string | null; fromMonth: string } | null;
  /** G-163 (D-78): salaried now or a salary scheduled — deactivating asks the last working day; owner-only. */
  salariedNowOrLater?: boolean;
  /** G-103 S2 (OD-84): 1–31, default 1. Present for every viewer. */
  payDayOfMonth?: number;
  workContractConfigured?: boolean;
  workContractOriginalName?: string | null;
  workContractMimeType?: string | null;
  workContractUploadedAt?: string | null;
  workContractUploadedBy?: { id: string; fullName: string } | null;
  shopPinConfigured?: boolean;
  /** G-85 S6: null = never chosen. Set from Manage team. */
  language?: AppLanguage | null;
  /*
   * G-136 — always sent by GET /users. Optional only because the schedule
   * builds stand-in rows from shifts (`scheduleCandidateUsers`), which know
   * nothing about accounts; absent there means "not known", never "active".
   */
  /** See `AccountStatus`. */
  accountStatus?: AccountStatus;
  /** When the live invitation's link runs out; null when there is none. */
  inviteExpiresAt?: string | null;
  /** The person proved their current address. */
  emailVerified?: boolean;
  /** A new address typed for them, waiting for its confirmation. */
  pendingEmail?: string | null;
  courierProfile?: CourierProfileSummary | null;
  locationAssignments: UserAssignmentSummary[];
}

export interface UpdateUserCompensationPayload {
  hourlyRateEur?: number | null;
  salesCommissionPercent?: number | null;
}

/** G-103 S2: owner-only. Omit to leave unchanged; `monthlySalaryEur: null` = hourly again. */
export interface UpdateUserPayTermsPayload {
  payDayOfMonth?: number;
  monthlySalaryEur?: number | null;
  /** G-163: "YYYY-MM", this month (default) or later; only with `monthlySalaryEur`. */
  salaryStartsFrom?: string;
}

export interface UpdateUserContactPayload {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string | null;
  email?: string;
  jobTitle?: string | null;
}

/** Self-service: a person's own name and phone (owner decision 2026-09-10). */
export interface UpdateMyContactPayload {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string | null;
}

export interface AssignUserLocationPayload {
  locationId: string;
  notes?: string;
}

export interface CreateLocationPayload {
  name: string;
  address: string;
}

export interface CreateZonePayload {
  name: string;
  zoneType: ZoneType;
}

export interface BalanceFilters {
  locationId?: string;
  zoneId?: string;
  itemId?: string;
  categoryId?: string;
  /** Only sent with Bucket/Vase category filter; API enforces category. */
  minDiameterCm?: number;
  minDepthCm?: number;
}

export type WasteCause =
  | "WILTED_ON_FLOOR"
  | "BROKEN_DURING_PREP"
  | "STAFF_ACCIDENT"
  | "CUSTOMER_RETURN"
  | "RECEIVING_DAMAGE"
  | "OTHER";

/** G-24: mirrors `FAULT_WASTE_CAUSES` in the backend's domain-enums. */
export const FAULT_WASTE_CAUSES: readonly WasteCause[] = [
  "BROKEN_DURING_PREP",
  "STAFF_ACCIDENT",
];

export interface Supplier {
  id: string;
  code: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  /** G-129: the server has a PDF reader for this supplier's papers. */
  hasParser: boolean;
}

export interface CreateSupplierPayload {
  code: string;
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive?: boolean;
}

export interface UpdateSupplierPayload {
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive?: boolean;
}

export interface ReceiveFlowerBatchPayload {
  itemId: string;
  supplierId: string;
  locationId: string;
  zoneId: string;
  invoicedQty: number;
  usableQty: number;
  invoiceTotalEur: number;
  /** G-121 S3: required — which side of IVA the total is on. */
  moneyBasis: TypedMoneyBasis;
  invoiceNumber?: string;
  purchasedAt: string;
  receivedAt: string;
}

export interface LogWastePayload {
  itemId: string;
  locationId: string;
  zoneId: string;
  batchId: string;
  quantity: number;
  wasteCause: WasteCause;
  notes?: string;
  /** G-84 (OD-47): answers to "Is this from box X?", resubmitted after a 409. */
  boxAnswers?: { boxId: string; takenFromBox: boolean }[];
}

export interface ActiveBatchRow {
  id: string;
  batchCode: string;
  remainingQty: string;
  zoneId: string;
  receivedAt: string;
  /** WO-B part 4: null when the lot's cost is unknown. */
  purchaseUnitCostEur: string | null;
}

export type ReceivedBatchSource =
  | "delivery"
  | "single_use"
  | "flower"
  | "adjustment"
  | "other";

export interface ReceivedBatchHistoryRow extends PurchaseMoneyFacts {
  id: string;
  batchCode: string;
  source: ReceivedBatchSource;
  status: string;
  invoicedQty: string;
  receivedQty: string;
  usableQty: string;
  receivingWasteQty: string;
  remainingQty: string;
  /** Before IVA. */
  invoiceTotalEur: string;
  /** Null exactly when `costSource` is UNKNOWN (WO-B part 4). */
  purchaseUnitCostEur: string | null;
  /** WO-B (OD-198): where `purchaseUnitCostEur` came from. */
  costSource: LotCostSource;
  /**
   * G-121 S3: the figure and side the correction box opens on, from the
   * server (money typed with IVA comes back as its gross on "Includes").
   * WO-B: `totalEur` is null for a lot with no price — the box opens empty.
   */
  correctionPrefill: { totalEur: string | null; moneyBasis: TypedMoneyBasis };
  invoiceNumber: string | null;
  purchasedAt: string;
  receivedAt: string;
  /** `category.vatRatePercent`: the CURRENT rate, which the switch names. */
  item: {
    id: string;
    name: string;
    defaultUom: DefaultUom;
    isPerishable: boolean;
    category: { vatRatePercent: string };
  };
  zone: { id: string; name: string };
  supplier: { id: string; name: string } | null;
  receivedByUser: { id: string; fullName: string; email: string };
}

export interface ReceivedBatchHistoryListResponse {
  items: ReceivedBatchHistoryRow[];
  total: number;
}

/**
 * WO-B (G-139, OD-198): where a lot's unit cost came from — a real price, a
 * cost borrowed from the item's last priced lot or its catalogue cost, or
 * nothing at all.
 */
export type LotCostSource = "PRICED" | "ESTIMATED_LAST_LOT" | "ESTIMATED_CATALOG" | "UNKNOWN";

export interface UpdateReceivedBatchPayload {
  reason: string;
  invoicedQty?: number;
  receivedQty?: number;
  usableQty?: number;
  remainingQty?: number;
  invoiceTotalEur?: number;
  /** G-121 S3: required with `invoiceTotalEur`. */
  moneyBasis?: TypedMoneyBasis;
  invoiceNumber?: string | null;
  supplierId?: string | null;
  purchasedAt?: string;
  receivedAt?: string;
}

export type BouquetTemplateLineKind =
  | "EXACT_ITEM"
  | "FLOWER_REQUIREMENT"
  | "PAPER_REQUIREMENT"
  | "RIBBON_REQUIREMENT";

export interface BouquetTemplateLineColor {
  id: string;
  color: { id: string; name: string; hex: string | null };
}

export interface BouquetTemplateLine {
  id?: string;
  kind?: BouquetTemplateLineKind;
  itemId?: string | null;
  subCategoryId?: string | null;
  colorId?: string | null;
  quantity: string | number;
  uom: DefaultUom;
  item?: { id: string; name: string; defaultUom: DefaultUom; category?: { code: string; label: string } } | null;
  subCategory?: SubCategory | null;
  color?: { id: string; name: string; hex: string | null } | null;
  /** Acceptable colors for PAPER_REQUIREMENT / RIBBON_REQUIREMENT lines. */
  acceptableColors?: BouquetTemplateLineColor[];
}

export interface BouquetTemplate {
  id: string;
  name: string;
  description: string | null;
  locationId: string | null;
  imageKey: string | null;
  isActive: boolean;
  lines: BouquetTemplateLine[];
}

export interface CreateBouquetTemplatePayload {
  name: string;
  description?: string;
  locationId?: string;
  imageKey?: string;
  isActive?: boolean;
  lines: Array<{
    kind: BouquetTemplateLineKind;
    itemId?: string;
    subCategoryId?: string;
    colorId?: string;
    /** Acceptable colors for PAPER_REQUIREMENT / RIBBON_REQUIREMENT lines. */
    colorIds?: string[];
    /** Required for EXACT_ITEM / FLOWER_REQUIREMENT; omit for paper/ribbon lines. */
    quantity?: number;
    uom?: DefaultUom;
  }>;
}

export interface UpdateBouquetTemplatePayload {
  name?: string;
  description?: string;
  locationId?: string | null;
  imageKey?: string | null;
  isActive?: boolean;
  lines?: CreateBouquetTemplatePayload["lines"];
}

export interface CreateBouquetBuildPayload {
  locationId: string;
  zoneId: string;
  templateId?: string;
  lines?: Array<{
    kind?: BouquetTemplateLineKind;
    itemId?: string;
    subCategoryId?: string;
    colorId?: string;
    quantity: number;
    uom?: DefaultUom;
  }>;
}

export type ConsumablePreset = "SMALL" | "MEDIUM" | "BIG";

export type BouquetPaperUsagePayload =
  | { itemId: string; batchId: string; sizePreset: ConsumablePreset }
  | { itemId: string; batchId: string; customCm: number };

export interface CreateBouquetCompletePayload extends CreateBouquetBuildPayload {
  paperUsages?: BouquetPaperUsagePayload[];
}

export type ShopFloorBuildBouquetPayload = Omit<
  CreateBouquetCompletePayload,
  "locationId" | "zoneId"
>;

export interface YieldRow {
  itemId: string;
  itemName: string;
  invoicedQty: string;
  usableQty: string;
  consumedQty: string;
  wasteQtyExclReceiving: string;
  invoiceTotalEur: string;
  boxOpenCostEur: string | null;
  trueCostEur: string | null;
  /** WO-B part 4: usable units with no supplier price, left out of both costs. */
  unpricedUsableQty: string;
  yieldPct: number | null;
}

export interface ItemYieldResponse {
  itemId: string;
  itemName: string;
  suppliers: Array<{
    supplierId: string | null;
    supplierCode: string | null;
    supplierName: string | null;
    invoicedQty: string;
    usableQty: string;
    consumedQty: string;
    wasteQtyExclReceiving: string;
    invoiceTotalEur: string;
    boxOpenCostEur: string | null;
    trueCostEur: string | null;
    yieldPct: number | null;
  }>;
}

/** CM batch with stock at shop (any zone). */
export interface PaperRollRow {
  batchId: string;
  batchCode: string;
  remainingQty: string;
  zoneId: string;
  zoneName: string;
  zoneType: ZoneType;
  item: {
    id: string;
    name: string;
    defaultUom: string;
    /** G-93: the roll's photo, so a florist can see the paper, not just read it. */
    imageKey?: string | null;
    category?: { code: string; label: string };
    /** Single color (e.g. a ribbon's). Null on uncolored rolls like plain kraft. */
    color?: { id: string; name: string; hex: string | null } | null;
  };
  openedAt: string | null;
}

/** @deprecated Use PaperRollRow — same shape, kept for gradual renames */
export type FrontRollRow = PaperRollRow;

/**
 * One tap-to-add card in a bouquet builder (shop-floor tablet and the phone
 * builder for managers/owners). Flowers only; ribbon/paper come from
 * {@link PaperRollRow}. Re-exported from `shopMode.ts` for the floor client.
 */
export interface BouquetBuilderItem {
  itemId: string;
  name: string;
  imageKey: string | null;
  categoryCode: string;
  categoryLabel: string;
  defaultUom: DefaultUom;
  color: { id: string; name: string; hex: string | null } | null;
  /** Controlled subcategory (flower type); null when untagged. */
  subCategory: { id: string; code: string; label: string } | null;
  /** Stock summed across all zones at the location. */
  qtyOnHand: string;
  /** Reorder threshold (low-stock cutoff); null when unset. */
  reorderThreshold: string | null;
  /** Cost-to-build per unit (oldest active batch's purchase cost); null if no batch. */
  fifoUnitCostEur: string | null;
  /** The price a person typed on the item, or null if nobody did. */
  defaultSaleUnitPriceEur: string | null;
  /**
   * G-45 Tier 2 — what this unit actually sells for: the typed price when there
   * is one, otherwise derived from the real batch cost and the category's
   * target margin. **Render this, not `defaultSaleUnitPriceEur`** — that is the
   * field the pickers used to read, which is why they said "Selling price
   * missing" for stock the app could price perfectly well. Null only when there
   * is neither a price nor any cost to work one out from.
   */
  saleUnitPriceEur: string | null;
  /** True when the price was worked out from cost rather than chosen by a person. */
  saleUnitPriceIsDerived: boolean;
}

/**
 * G-12 — one photo of a finished bouquet. `id` is null for the read-only
 * DELIVERY_PROOF entry, which is the courier's own delivery photo surfaced
 * through the build → sale → job chain rather than copied here.
 */
export interface BouquetBuildPhoto {
  id: string | null;
  origin: "BUILD" | "DELIVERY_PROOF";
  deliveryJobId?: string;
  mimeType: string | null;
  uploadedBy: { id: string; fullName: string } | null;
  createdAt: string | null;
}

export interface ConsumableRollSummary {
  batchId: string;
  batchCode: string;
  itemId: string;
  itemName: string;
  invoiceTotalEur: string;
  remainingQty: string;
  batchStatus: string;
  bouquetsAttributedCount: number;
  costPerBouquetEur: string | null;
  /** G-175 (KI-154): why `costPerBouquetEur` is null; null when it is a figure. */
  costPerBouquetMissing: "SPLIT_PART" | "NO_PRICE" | "NO_BOUQUETS" | null;
  totalCmConsumedOnBatch: string;
}

export interface ActiveConsumableRollResponse {
  id: string;
  batch: { id: string; batchCode: string; remainingQty: string };
  item: { id: string; name: string; defaultUom: string };
  zone: { id: string; name: string; zoneType: ZoneType };
}

export type SaleLineKind = "BOUQUET" | "ITEM";
export type SalePaymentType = "CASH" | "CARD";

/**
 * G-141 (OD-215): one part of how the customer paid. One part may leave the
 * amount out (it takes the whole total); cash + card carry both amounts,
 * which must add up to the total.
 */
export interface PaymentPart {
  method: SalePaymentType;
  amountEur?: number;
}

/**
 * G-141 (OD-215): the office "Add a sale" also takes ONLINE — a card payment
 * link, a bank transfer, Bizum or the Shopify checkout. Never the kiosk.
 */
export type OfficePaymentMethod = SalePaymentType | "ONLINE";

export interface OfficePaymentPart {
  method: OfficePaymentMethod;
  amountEur?: number;
}

/** A recorded payment row on a sale (BANK_TRANSFER only on old data). */
export interface SalePayment {
  method: OfficePaymentMethod | "BANK_TRANSFER";
  amountEur: string;
}
export type BouquetBuildStatus = "DRAFT" | "FINALIZED" | "SOLD" | "VOIDED";

export interface BouquetBuildSummary {
  id: string;
  status: BouquetBuildStatus;
  /** WO-B (OD-198): the known part when `unknownCostComponentCount` > 0.
   *  Absent for a florist — the server leaves every cost out (KI-152). */
  totalCogsEur?: string | null;
  /** WO-B (OD-198): components with any part of unknown cost. */
  unknownCostComponentCount?: number;
  suggestedSalePriceEur: string | null;
  salePriceEur: string | null;
  /** Absent for a florist (KI-152). */
  grossProfitEur?: string | null;
  soldAt: string | null;
  createdAt: string;
  location: { id: string; name: string; code: string };
  zone: { id: string; name: string; zoneType: ZoneType };
  template: { id: string; name: string; imageKey?: string | null } | null;
  builtBy: { id: string; fullName: string; email: string };
  /** The build's own name (G-132 shows it on the ready-bouquets cards). */
  name?: string | null;
  /** First build photo, if any; only the in-stock list selects it. */
  photoId?: string | null;
  /** OD-31 / G-112: the order holding this build (built for it, or taken
   *  off the shelf as a ready-made bouquet) — listed as stock, never
   *  sellable as a walk-in (the server refuses it too). Derived by the
   *  server's in-stock list; a CANCELLED order frees its bouquet (OD-32). */
  forOrder?: { id: string; code: string } | null;
  shopifyOrderLine?: {
    id: string;
    orderId: string;
    order: { status: string };
  } | null;
  lines: Array<{
    id: string;
    quantity: string;
    uom: DefaultUom;
    item: { id: string; name: string; defaultUom: DefaultUom };
  }>;
}

export interface BouquetBuildLineOnSale {
  id: string;
  quantity: string;
  uom: DefaultUom;
  item: { id: string; name: string; defaultUom: DefaultUom };
  allocations: Array<{
    id: string;
    qty: string;
    /** WO-B (OD-198): null together when the lot's cost is unknown. */
    unitCostEur: string | null;
    lineCostEur: string | null;
    batch: { id: string; batchCode: string };
  }>;
}

/** Bouquet snapshot on a sale line (includes composition for custom bouquets). */
export interface BouquetBuildOnSaleLine {
  id: string;
  totalCogsEur: string | null;
  createdAt: string;
  template: { id: string; name: string } | null;
  zone: { id: string; name: string; zoneType: ZoneType };
  builtBy: { id: string; fullName: string };
  lines: BouquetBuildLineOnSale[];
}

export interface SaleLine {
  id: string;
  kind: SaleLineKind;
  quantity: string;
  uom: DefaultUom | null;
  suggestedUnitPriceEur: string | null;
  unitPriceEur: string;
  lineTotalEur: string;
  /** G-30: the IVA rate frozen on the line (G-162: the refund window shows it). */
  vatRatePercent: string;
  /** WO-B (OD-198): the known part when `unknownCostQty` > 0. */
  lineCogsEur: string;
  /** WO-B (OD-198): units of unknown cost (a bouquet of incomplete cost: 1). */
  unknownCostQty: string;
  /** Null — "cannot check" — exactly when part of the cost is unknown. */
  floorPriceEur: string | null;
  zoneId: string | null;
  bouquetBuildId: string | null;
  itemId: string | null;
  item: { id: string; name: string; defaultUom: DefaultUom } | null;
  bouquetBuild: BouquetBuildOnSaleLine | null;
  /** G-151: on an undone sale, the bouquet this line sold (released for sale again). */
  voidedBouquetBuild: BouquetBuildOnSaleLine | null;
  allocations: Array<{
    id: string;
    qty: string;
    /** WO-B (OD-198): null together when the lot's cost is unknown. */
    unitCostEur: string | null;
    lineCostEur: string | null;
    batch: { id: string; batchCode: string };
  }>;
}

/** G-151: an undone sale is VOIDED — kept as history, counted in no total. */
export type SaleStatus = "ACTIVE" | "VOIDED";

export interface Sale {
  id: string;
  soldAt: string;
  status: SaleStatus;
  /** G-151: who undid the sale, when and why — set exactly when VOIDED. */
  voidedAt: string | null;
  voidReason: string | null;
  voidedBy: { id: string; fullName: string } | null;
  notes: string | null;
  totalEur: string;
  /** WO-B (OD-198): the known part when `unknownCostLineCount` > 0. */
  totalCogsEur: string;
  grossProfitEur: string;
  /** WO-B (OD-198): lines whose cost is partly unknown. */
  unknownCostLineCount: number;
  commissionPercent: string | null;
  location: { id: string; name: string; code: string };
  soldBy: { id: string; fullName: string; email: string; role: Role };
  /** G-141: none on a sale that completed an order (the order holds its money). */
  payments: SalePayment[];
  /** G-141: the order this sale completed, if any — its money is the order's.
   *  G-162: `shopifyOrder` set when that order mirrors a web order (refunded in Shopify). */
  customerOrder: {
    id: string;
    code: string;
    spawnedFromSale: boolean;
    shopifyOrder?: { id: string } | null;
  } | null;
  /** G-141: the web order this sale fulfilled — paid online at the Shopify checkout. */
  shopifyOrder: { id: string; orderNumber: string | null } | null;
  /** G-162: money given back on this sale, removed refunds included (marked). */
  refunds: SaleRefundRow[];
  lines: SaleLine[];
  /**
   * G-169: the sale's own figures, worked out by the server
   * (`src/modules/sales/sale-figures.ts`): where its money lives, what was
   * refunded and what is left to refund, and how far the price charged was
   * from the till's suggestion (positive = under; null = nothing to compare).
   */
  moneyHome: SaleMoneyHome;
  refundedEur: string;
  refundableEur: string;
  suggestedDifferenceEur: string | null;
  /** G-175: the IVA inside the goods, at each line's frozen rate. */
  goodsVatEur: string;
  /**
   * G-175 (KI-47): the glossary's Margin — total − goods IVA − cost of goods,
   * net; the known part when `unknownCostLineCount` > 0. Never sent to a florist.
   */
  marginEur: string;
}

/** G-141 / G-169: the one place a sale's money is (the server's `saleMoneyHome`). */
export type SaleMoneyHome = "rows" | "order" | "shopify" | "notRecorded";

/**
 * G-169 (WO-D): a period's totals as `GET /sales/totals` and `/sales/me/totals`
 * answer them — the one server rule (`src/modules/sales/sale-period-totals.ts`)
 * the Sales records tiles, the Profile's "My sales" and a manager's Finances
 * tiles read. Refunds given in the period are already off every figure;
 * `soldEur` is revenue before them; `tickets` counts the sales that happened.
 */
export interface SalePeriodTotals {
  revenueEur: string;
  soldEur: string;
  /** The known part only; see `unknownCostSales` (WO-B). */
  cogsEur: string;
  /** "Margin with IVA": revenue − cost of goods (gross minus net). */
  grossProfitEur: string;
  /** G-175: the IVA inside the goods sold, refunds off. */
  goodsVatEur: string;
  /** G-175 (KI-47): revenue − goods IVA − cost of goods (net); the known part. Never sent to a florist. */
  marginEur: string;
  tickets: number;
  unknownCostSales: number;
  /** The refund events' net amount (negative when more was removed than given). */
  refundsEur: string;
  refundCount: number;
  paid: {
    cash: string;
    card: string;
    online: string;
    onOrders: string;
    notRecorded: string;
    /** What refund events took off each column (already subtracted). */
    refunded: { cash: string; card: string; online: string; onOrders: string };
  };
}

/** G-162: a refund as its sale's row shows it. */
export interface SaleRefundRow {
  id: string;
  refundedAt: string;
  method: SalePayment["method"];
  reason: string;
  /** Gross. */
  totalEur: string;
  /** Net: the known cost of the units put back on the shelf. */
  cogsReturnedEur: string;
  /** Null when Shopify gave it (G-166, `shopifyRefundId`). */
  refundedBy: { id: string; fullName: string } | null;
  /** Set when the owner removed it: it then counts nowhere. */
  removedAt: string | null;
  removeReason: string | null;
  removedBy: { id: string; fullName: string } | null;
  /** The order's REFUND ledger row, when the money went back through an order. */
  orderPaymentId: string | null;
  /** G-166: refunded in Shopify (a web order's sale); cannot be removed here. */
  shopifyRefundId: string | null;
  /** G-166: when the stock question of a Shopify refund was answered (null = open, or none). */
  stockCheckedAt: string | null;
  /** G-166: the server's one rule — a Shopify refund of item lines whose stock is still to check. */
  stockOpen: boolean;
  lines: Array<{ saleLineId: string; amountEur: string; restockedQty: string }>;
}

/** G-166: a Shopify refund whose stock question is open (Home, Sales records). */
export interface RefundStockToCheck {
  refundId: string;
  saleId: string;
  refundedAt: string;
  totalEur: string;
  location: { id: string; name: string };
  soldAt: string;
  orderNumber: string | null;
}

/** G-166: what the stock question asks — the refunded lines, their money locked. */
export interface RefundStockQuestion {
  refundId: string;
  saleId: string;
  refundedAt: string;
  totalEur: string;
  stockCheckedAt: string | null;
  lines: Array<{
    saleLineId: string;
    kind: "ITEM" | "BOUQUET" | null;
    amountEur: string;
    restockedQty: string;
    /** Units that may still go back on the shelf ("0" for a bouquet). */
    restockableQty: string;
  }>;
}

/**
 * G-162: one refund money event in a period, with what it takes off every
 * total — worked out on the server (`sale-refunds.ts`). A REFUND counts on
 * the day it was given; a REMOVAL (owner 2026-10-19) adds it back on the day
 * the owner removed it, so its figures are negative.
 */
export interface PeriodSaleRefund {
  /** Unique per event (a refund removed in the same period appears twice). */
  key: string;
  id: string;
  kind: "REFUND" | "REMOVAL";
  saleId: string;
  /** When the money moved. */
  at: string;
  refundedAt: string;
  /** Null when Shopify gave it (G-166). */
  refundedBy: { id: string; fullName: string } | null;
  reason: string;
  method: SalePayment["method"];
  /** The money went back through a customer order's ledger ("Paid on orders"). */
  throughOrder: boolean;
  /** G-166: refunded in Shopify (a web order's sale). */
  viaShopify: boolean;
  location: { id: string; name: string; code: string };
  sale: {
    soldAt: string;
    soldBy: { id: string; fullName: string };
    customerOrder: { id: string; code: string; spawnedFromSale: boolean } | null;
  };
  /** Gross; negative for a REMOVAL. */
  totalEur: string;
  vatEur: string;
  /** Net. */
  cogsReturnedEur: string;
  /** totalEur − cogsReturnedEur: what the stored gross profit loses. */
  grossProfitEur: string;
  /** What the seller's commission loses; null unless the reader may see pay (owner, or one's own). */
  commissionEur: string | null;
}

/** G-162: how a refund's money goes back. */
export type RefundMethod = "CASH" | "CARD" | "ONLINE" | "BANK_TRANSFER";

export interface SaleRefundLinePayload {
  saleLineId: string;
  /** Omitted = the whole of what is left of the line. */
  amountEur?: number;
  /** Units back on the shelf (item lines only). */
  restockQty?: number;
}

export interface CreateSaleRefundPayload {
  lines: SaleRefundLinePayload[];
  method: RefundMethod;
  reason: string;
}

/** G-162: the refund window's live figures (`POST /sales/:id/refunds/preview`). */
export interface SaleRefundPreview {
  saleId: string;
  seller: { id: string; fullName: string };
  /** The order whose ledger the money goes back through; null = the sale's own money. */
  throughOrder: { id: string; code: string } | null;
  allowedMethods: RefundMethod[];
  suggestedMethod: RefundMethod | null;
  lines: Array<{
    saleLineId: string;
    kind: SaleLineKind;
    leftEur: string;
    /**
     * "N of M" (owner 2026-10-20): the most this line may put back on the
     * shelf given the money asked on its item in these lines — it follows
     * the amount typed; "0" for a bouquet or an unticked item line.
     */
    restockableQty: string;
  }>;
  totalEur: string;
  vatEur: string;
  cogsReturnedEur: string;
  /** Units going back — a restock the amount no longer covers is clamped here. */
  restockedUnits: string;
  /** Owner only (pay is owner-only); null for a manager. */
  commissionEur: string | null;
  countsOn: string;
}

export interface CreateSaleLinePayload {
  kind: SaleLineKind;
  bouquetBuildId?: string;
  itemId?: string;
  zoneId?: string;
  quantity: number;
  unitPriceEur: number;
}

export interface CreateSalePayload {
  locationId: string;
  soldAt?: string;
  notes?: string;
  /** G-141 (OD-215): how the customer paid — online, cash, card, or cash + card. */
  payments: OfficePaymentPart[];
  lines: CreateSaleLinePayload[];
  /** G-84 (OD-47): answers to "Is this from box X?", resubmitted after a 409. */
  boxAnswers?: { boxId: string; takenFromBox: boolean }[];
}

/**
 * G-141 (owner 2026-10-13): "Ring up" — the sale that completes an order,
 * saved with the completion. The shop is the order's; `method` is required
 * while the ticket plus the courier fee is more than the order's paid total.
 */
export interface RingUpOrderPayload {
  soldAt?: string;
  notes?: string;
  lines: CreateSaleLinePayload[];
  method?: StaffOrderPaymentMethod;
  boxAnswers?: { boxId: string; takenFromBox: boolean }[];
}

export type ShiftStatus =
  | "SCHEDULED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "MISSED"
  | "CANCELED";

/** G-123: the caller's own earnings for one work day (OD-141 attribution). */
export interface MyWorkDayEarnings {
  workDate: string;
  workedMinutes: number;
  /** null when worked time has no hourly rate, or the month is salaried (KI-169). */
  wageEur: string | null;
  commissionEur: string;
  /** wage + commission; the commission alone in a salaried month. */
  totalEur: string | null;
  missingHourlyRate: boolean;
  /** KI-169: the day's month pays a salary — no wage here. */
  salaried: boolean;
}

export interface Shift {
  id: string;
  date: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  /** D-90: present for an owner/admin viewer only — absent, not null, for anyone else (OD-79). */
  payrollHourlyRateEur?: string | null;
  status: ShiftStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  location: { id: string; name: string; code: string };
  user: { id: string; fullName: string; email: string; role: Role };
  createdBy: { id: string; fullName: string };
  lastEditedBy: { id: string; fullName: string } | null;
}

export interface CreateShiftPayload {
  locationId: string;
  userId: string;
  plannedStart: string;
  plannedEnd: string;
  notes?: string;
  payrollHourlyRateEur?: number | null;
}

export interface BulkCreateShiftsPayload {
  locationId: string;
  userId: string;
  fromDate: string;
  toDate: string;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  notes?: string;
  payrollHourlyRateEur?: number | null;
}

export interface BulkCreateShiftsResponse {
  created: number;
  skippedOverlap: number;
  /** G-8: days skipped because the worker has approved vacation. */
  skippedVacation: number;
  ids: string[];
}

export interface UpdateShiftPayload {
  plannedStart?: string;
  plannedEnd?: string;
  actualStart?: string | null;
  actualEnd?: string | null;
  status?: ShiftStatus;
  notes?: string | null;
  payrollHourlyRateEur?: number | null;
}

export interface ListShiftsQuery {
  locationId?: string;
  from?: string;
  to?: string;
  userId?: string;
}

export interface ListMyShiftsQuery {
  from?: string;
  to?: string;
}

export type TaskStatus = "PENDING" | "COMPLETED" | "CANCELLED";
export type TaskRecurrenceKind = "EVERY_N_DAYS" | "WEEKLY";

export interface Task {
  id: string;
  scheduleId: string | null;
  name: string;
  description: string | null;
  notes: string | null;
  scheduledDate: string;
  scheduledFor: string;
  status: TaskStatus;
  requireCompletionPhoto: boolean;
  hasReferenceImage: boolean;
  hasCompletionImage: boolean;
  location: { id: string; name: string; code: string };
  assignee: { id: string; fullName: string; role: Role } | null;
  createdBy: { id: string; fullName: string };
  completedBy: { id: string; fullName: string } | null;
  completedAt: string | null;
  cancelledBy: { id: string; fullName: string } | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FloorTaskTodayWorkItem {
  kind: "TASK";
  id: string;
  scheduledFor: string;
  task: Task;
}

export interface FloorDeliveryTodayWorkItem {
  kind: "DELIVERY";
  id: string;
  name: string;
  scheduledFor: string;
  status: DeliveryStatus;
  supplier: { id: string; name: string; code: string; isActive: boolean };
  confirmedLineCount: number;
  totalLineCount: number;
}

export type FloorTodayWorkItem =
  | FloorTaskTodayWorkItem
  | FloorDeliveryTodayWorkItem;

export interface TaskSchedule {
  id: string;
  name: string;
  description: string | null;
  notes: string | null;
  recurrenceKind: TaskRecurrenceKind;
  intervalDays: number | null;
  intervalWeeks: number | null;
  weekdays: number[];
  timeOfDayMinutes: number;
  startDate: string;
  endDate: string | null;
  requireCompletionPhoto: boolean;
  isActive: boolean;
  materializedThrough: string | null;
  hasReferenceImage: boolean;
  location: { id: string; name: string; code: string };
  assignee: { id: string; fullName: string; role: Role } | null;
  createdBy: { id: string; fullName: string };
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTaskPayload {
  name?: string;
  description?: string | null;
  notes?: string | null;
  scheduledFor?: string;
  assigneeUserId?: string | null;
  requireCompletionPhoto?: boolean;
}

export interface CreateTaskSchedulePayload {
  locationId: string;
  assigneeUserId?: string | null;
  name: string;
  description?: string | null;
  notes?: string | null;
  recurrenceKind: TaskRecurrenceKind;
  intervalDays?: number;
  intervalWeeks?: number;
  weekdays?: number[];
  timeOfDayMinutes: number;
  startDate: string;
  endDate?: string | null;
  requireCompletionPhoto?: boolean;
}

export interface UpdateTaskSchedulePayload {
  assigneeUserId?: string | null;
  name?: string;
  description?: string | null;
  notes?: string | null;
  recurrenceKind?: TaskRecurrenceKind;
  intervalDays?: number | null;
  intervalWeeks?: number | null;
  weekdays?: number[];
  timeOfDayMinutes?: number;
  startDate?: string;
  endDate?: string | null;
  requireCompletionPhoto?: boolean;
  isActive?: boolean;
}

export interface ListTasksQuery {
  locationId?: string;
  from?: string;
  to?: string;
  status?: TaskStatus;
  assigneeUserId?: string;
}

export interface AssignableUser {
  id: string;
  fullName: string;
  role: Role;
}

export interface ListMySalesQuery {
  from?: string;
  to?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export type DeliveryStatus =
  | "SCHEDULED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELED";

export type DeliveryLineStatus =
  | "PENDING"
  | "CONFIRMED_AS_EXPECTED"
  | "CONFIRMED_WITH_DIFFERENCE"
  | "MISSING";

/** G-59: how a receiving line was found before it was confirmed. */
export type DeliveryLineFoundBy = "SCANNED" | "TYPED" | "TAPPED";

/**
 * G-121 S2: one delivery line's money as the SERVER values it
 * (`src/modules/deliveries/delivery-money.ts` is the one rule; the web only
 * reads it). Decimal strings. The three "once confirmed" figures are null
 * while the line is PENDING. A shortage is information — billed but not
 * received — never a receivable.
 */
export interface DeliveryLineMoney {
  /** round4(billedTotal ÷ billedQty); null when the line has no invoice. */
  billedUnitPriceEur: string | null;
  /** Which unit price the shortage and overage are valued at. */
  pricedFrom: "BILLED" | "EXPECTED";
  /** 2 dp; null while PENDING. */
  receivedValueEur: string | null;
  /** 2 dp; null while PENDING; "0.00" when none. */
  shortageEur: string | null;
  /** 2 dp; null while PENDING; "0.00" when none. */
  overageEur: string | null;
}

/** G-121 S2: the delivery's money, summed by the server from its lines. */
export interface DeliveryMoneyTotals {
  expectedEur: string;
  /** G-169: Σ the lines' typed gross (IVA inside); non-null ONLY when every line has one. */
  expectedGrossEur: string | null;
  /** Non-null ONLY when every line carries a billed total — never a partial sum. */
  billedEur: string | null;
  receivedEur: string;
  shortageEur: string;
  overageEur: string;
  /** Lines whose status is not PENDING. */
  confirmedLines: number;
  totalLines: number;
  /** Lines with a shortage quantity above zero or marked MISSING. */
  shortLines: number;
}

export interface ScheduledDeliveryLine {
  id: string;
  itemId: string;
  zoneId: string;
  expectedQty: string;
  expectedUnitCostEur: string;
  expectedTotalEur: string;
  receivedQty: string | null;
  shortageQty: string | null;
  overageQty: string | null;
  status: DeliveryLineStatus;
  confirmedAt: string | null;
  notes: string | null;
  batchId: string | null;
  /** G-59: null on lines confirmed before scan-to-confirm existed. */
  foundBy: DeliveryLineFoundBy | null;
  /**
   * G-121 S2: what the supplier's paper says for this line, copied from the
   * invoice line at commit. Both null on a delivery typed by hand. Beside the
   * expected figures, never in their place.
   */
  billedQty: string | null;
  billedTotalEur: string | null;
  /** G-121 S2: of the units that arrived, how many are damaged — a note with a photo; still stock. */
  damagedQty: string | null;
  /**
   * G-121 S3: how the expected money was given. `expected*` stay before
   * IVA; a typed line keeps the rate, IVA and gross beside them.
   */
  moneyBasis: PurchaseMoneyBasis | null;
  ivaRatePercent: string | null;
  expectedIvaEur: string | null;
  expectedGrossTotalEur: string | null;
  /** G-121 S2: the server's valuation of this line. */
  money: DeliveryLineMoney;
  item: {
    id: string;
    name: string;
    defaultUom: DefaultUom;
    isPerishable: boolean;
    /** The product photo shown on the receiving row; null when none is set. */
    imageKey: string | null;
    /** G-59: what a scan matches; null until captured or promoted with one. */
    barcode: string | null;
    sku: string | null;
    /** Every supplier's codes for the item; filter by the delivery's supplier. */
    supplierItemAliases: Array<{ supplierId: string; supplierSku: string }>;
    /** G-130: the shop's own code (G-99) — the sticker prints this when present. */
    issuedBarcode: { code: string; barcodeText: string } | null;
  };
  zone: { id: string; name: string; zoneType: ZoneType };
  confirmedBy: { id: string; fullName: string } | null;
  /**
   * G-131: whether "Undo receiving" is open to this line — the server's
   * `receiveUndoState`, so the button never promises what the server refuses.
   * `undoBlockedBy` says why not (null when it can, or while pending).
   */
  canUndoReceive: boolean;
  undoBlockedBy: string | null;
  /**
   * The batch the arrived units went into (damaged ones included); null
   * while PENDING and on a line of which nothing arrived.
   */
  batch: {
    id: string;
    batchCode: string;
    usableQty: string;
    /** Billed units that never arrived (their cost is in the unit below). */
    receivingWasteQty: string;
    /** 4 dp: the line's billed money spread over the units that arrived. Nullable with the lot's column (WO-B part 4); a delivery lot is always priced. */
    purchaseUnitCostEur: string | null;
    /** The receive movement carrying the damage photo; null when nothing arrived damaged. */
    damagePhotoMovementId: string | null;
  } | null;
}

/** Present when this delivery was created from invoice commit (audit link). */
export interface DeliveryInvoiceLink {
  id: string;
  invoiceNumber: string | null;
  baseEur?: string | null;
  grandTotalEur?: string | null;
}

export interface ScheduledDelivery {
  id: string;
  scheduledFor: string;
  invoiceNumber: string | null;
  notes: string | null;
  status: DeliveryStatus;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  supplier: { id: string; name: string; code: string; isActive: boolean };
  location: { id: string; name: string; code: string };
  createdBy: { id: string; fullName: string };
  invoice: DeliveryInvoiceLink | null;
  lines: ScheduledDeliveryLine[];
  /** G-121 S2: the server's money for the whole delivery. */
  totals: DeliveryMoneyTotals;
  /**
   * G-131 (OD-163 / OD-164 / OD-174): a delivery typed by hand waits for the
   * factura that bills it once something has arrived. "not-arrived" = typed
   * by hand, nothing received yet; "not-applicable" = it came from a paper,
   * or is cancelled.
   */
  facturaWait: DeliveryFacturaWait;
  coveredByFactura: { id: string; label: string } | null;
  facturaWaitDismissal: { at: string; by: { id: string; fullName: string }; reason: string } | null;
  /** G-137 (OD-184): live documents (photos / PDFs) attached to a delivery typed by hand. */
  documentCount: number;
  /**
   * G-137: "missing" = typed by hand, arrived, no factura covers it and no
   * document — flagged, never blocking. "covered" = a factura covers it and it
   * has no document of its own; "not-applicable" = from a paper, or cancelled.
   */
  documentState: DeliveryDocumentState;
  /** G-137: why "No factura will come" is refused right now (no document yet), or null. */
  noFacturaBlockedBy: string | null;
}

export type DeliveryFacturaWait = "not-applicable" | "not-arrived" | "covered" | "waiting" | "dismissed";

export type DeliveryDocumentState = "not-applicable" | "not-arrived" | "covered" | "missing" | "attached";

/** G-137: one photo or PDF of a delivery typed by hand. */
export interface DeliveryDocument {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: { id: string; fullName: string };
}

export type InvoiceWorkflowStatus =
  | "UPLOADED"
  | "PARSING"
  | "PARSED"
  | "NEEDS_REVIEW"
  | "COMMITTED"
  | "FAILED";

export interface InvoiceLineMatchHints {
  status: string;
  itemId?: string;
  reason?: string;
  /** OD-112, on a code match: the item's current name and whether it differs from the printed text. */
  itemName?: string;
  printedNameDiffers?: boolean;
}

export interface InvoiceLineRow {
  id: string;
  lineIndex: number;
  supplierSku: string | null;
  barcodeRaw: string | null;
  descriptionRaw: string;
  quantity: string;
  unitPriceEur: string;
  lineTotalEur: string;
  /**
   * G-121 S1: what the paper prints BESIDE the net figures, each null when
   * the document prints no such cell — never 0 for unknown. Decimal strings:
   * percents to 2 dp, the gross to 2 dp, the list price to 4 dp.
   */
  ivaRatePercent: string | null;
  lineGrossEur: string | null;
  discountPercent: string | null;
  listUnitPriceEur: string | null;
  matchHints?: InvoiceLineMatchHints | null;
  itemId: string | null;
  zoneId: string | null;
  /** G-121 S2: the delivery line this row became at commit; null before, or when the line was deleted. */
  deliveryLineId: string | null;
}

/** G-121 S1: one IVA rate as the document's totals block prints it. */
export interface InvoiceVatBreakdownRow {
  id: string;
  ratePercent: string;
  baseEur: string;
  /** Null only when the paper prints the base per rate but no cuota. */
  ivaEur: string | null;
}

/**
 * G-121 S1: where an invoice's header money came from. PRINTED = read off
 * the paper; DERIVED = IVA computed as total − base because the paper printed
 * no IVA line; BACKFILLED_FROM_AUDIT = copied from the parser's earlier audit
 * by the migration, not re-read from the paper.
 */
/** G-164: TYPED = a person typed base and IVA on the paper's page (the total is base + IVA). */
export type InvoiceMoneySource = "PRINTED" | "DERIVED" | "BACKFILLED_FROM_AUDIT" | "TYPED";

export interface InvoicePreviewLine extends InvoiceLineRow {
  matchHints: InvoiceLineMatchHints;
  /**
   * G-129: the staging draft still waiting for this line — the app could not
   * create its item by itself (no category suggestion, an unknown code, a
   * same-name twin). Null when the line has an item, or when the same product
   * is already waiting from another paper.
   */
  draft: CatalogDraft | null;
}

/**
 * G-66: the parser's own report on the document, stored inside
 * `Invoice.parsedPayload`. Every parser has always produced these; nothing
 * rendered them, so a totals mismatch or an unmatched product row was written
 * to the database and never shown to anyone.
 */
export interface ParsedInvoiceBandAuditView {
  /** Supplier VAT band letter: "R" (flowers/plants) or "G" (everything else). */
  ivaCode: string;
  documentBaseEur: string;
  linesSumEur: string;
  /** `null` = the stored payload did not say. NEVER coerce that to agreement. */
  aligned: boolean | null;
}

export interface ParsedInvoiceTotalsAuditView {
  linesSumEur: string;
  documentTotalEur: string | null;
  grandTotalEur?: string | null;
  /**
   * G-121 S1: which printed figure `documentTotalEur` is — the taxable BASE
   * or a GROSS total. `null` = the payload did not say (parsers < 1.8.0, or
   * a layout whose total's basis the parser could not tell).
   */
  documentTotalKind: "BASE" | "GROSS" | null;
  /** `null` = the stored payload did not say. NEVER coerce that to agreement. */
  totalsAligned: boolean | null;
  bands?: ParsedInvoiceBandAuditView[];
}

export interface ParsedInvoiceReport {
  warnings: string[];
  totalsAudit: ParsedInvoiceTotalsAuditView | null;
}

/**
 * G-121 S4 (OD-142/OD-143): what an uploaded supplier paper is FOR. An albarán
 * schedules a delivery (stock only); a factura is the bill (IVA paid + paid
 * state); Deco's factura comes with the goods and does both; an items-only
 * upload counts for nothing. Mirrors Prisma's `PaperUse`.
 */
export type PaperUse = "ALBARAN" | "FACTURA" | "FACTURA_WITH_GOODS" | "ITEMS_ONLY" | "CREDIT_NOTE";

/**
 * G-121 S4 (OD-144): the 409 body of an upload of a file already in the app —
 * described only to someone who may open that paper.
 */
export type DuplicatePaperRef =
  | {
      id: string;
      status: InvoiceWorkflowStatus;
      paperUse: PaperUse;
      invoiceNumber: string | null;
      locationName: string;
      createdAt: string;
      canOpen: true;
    }
  | { canOpen: false };

/** G-121 S4 (OD-146): how a supplier factura was paid. */
export type SupplierPaymentMethod = "BANK_TRANSFER" | "DIRECT_DEBIT" | "CARD" | "CASH";

/**
 * G-121 S4 (OD-146): paid when ordered (the default — the owner pays when
 * ordering), or — once marked not paid yet — unpaid / part paid / paid from
 * the live payments; TOTAL_NOT_READ when there is no total to owe against.
 */
export type InvoicePaymentState =
  | "PAID_WHEN_ORDERED"
  | "UNPAID"
  | "PART_PAID"
  | "PAID"
  | "TOTAL_NOT_READ";

export interface InvoicePaymentRow {
  id: string;
  /** YYYY-MM-DD */
  paidOn: string;
  amountEur: string;
  method: SupplierPaymentMethod;
  note: string | null;
  recordedBy: { id: string; fullName: string };
  recordedAt: string;
  removedAt: string | null;
  removedBy: { id: string; fullName: string } | null;
  removeReason: string | null;
}

/** G-121 S4 (OD-146/OD-147): a factura's paid state — owner only. */
export interface InvoicePaymentsView {
  invoiceId: string;
  isBill: boolean;
  /** When this upload is a copy of the factura that counts. */
  copyOf: { id: string } | null;
  awaitingPayment: boolean;
  awaitingSetAt: string | null;
  awaitingSetBy: { id: string; fullName: string } | null;
  grandTotalEur: string | null;
  paidEur: string | null;
  owedEur: string | null;
  /** G-173: Σ the linked credit notes' totals (≤ 0, "0.00" with none); null when one is unknown. */
  creditedEur: string | null;
  /** G-173: total + credited — what the factura asks to be paid. */
  toPayEur: string | null;
  /** G-173: paid above "to pay"; "0.00" when not. */
  paidOverEur: string | null;
  state: InvoicePaymentState;
  payments: InvoicePaymentRow[];
}

export interface RecordInvoicePaymentPayload {
  paidOn: string;
  amountEur: number;
  method: SupplierPaymentMethod;
  note?: string;
}

/** G-121 S4 (OD-145): one purchase typed by hand, as the server summed it. */
export interface TypedPurchaseRow {
  source: "RECEIVE" | "MISSING_DELIVERY_LINE";
  id: string;
  at: string;
  location: { id: string; name: string; code: string };
  supplier: { id: string; name: string } | null;
  item: { id: string; name: string };
  category: { id: string; label: string };
  basis: TypedMoneyBasis;
  ivaRatePercent: string;
  netEur: string;
  ivaEur: string;
  grossEur: string;
  deliveryId: string | null;
  /** G-131: "dismissed" = the owner said no factura will come (IVA not deductible). */
  facturaWait: "waiting" | "dismissed";
  /** G-164: the linked factura the card cannot count; the typed IVA stands in for it. */
  uncountedFactura: { id: string; label: string } | null;
}

export interface MoneyTriple {
  netEur: string;
  ivaEur: string;
  grossEur: string;
}

/** G-121 S4 (OD-145): the "already typed by hand?" hint on a factura's page. */
export type HandTypedNearby =
  | { applicable: false; fromDate: null; toDate: null; rows: []; totals: null }
  | { applicable: true; fromDate: string; toDate: string; rows: TypedPurchaseRow[]; totals: MoneyTriple };

export interface CountedMoney extends MoneyTriple {
  count: number;
}

export interface PurchasePaperRef {
  id: string;
  supplierName: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  locationName: string;
  paperUse: PaperUse;
  status: InvoiceWorkflowStatus;
  /** G-131: the factura that counts this albarán's money, when linked. */
  coveredByFactura: { id: string; label: string } | null;
}

export interface PurchaseFacturaRow extends PurchasePaperRef {
  baseEur: string | null;
  ivaEur: string;
  grandTotalEur: string | null;
  readerBundlesSeveral: boolean;
  payment: {
    state: InvoicePaymentState;
    paidEur: string | null;
    owedEur: string | null;
    /** G-173: what its linked credit notes take off (≤ 0). */
    creditedEur: string | null;
    toPayEur: string | null;
  };
}

/** G-173: a credit note counted this month by its own date — figures negative, as printed. */
export interface PurchaseCreditNoteRow extends PurchasePaperRef {
  baseEur: string | null;
  ivaEur: string;
  grandTotalEur: string | null;
  /** The factura it corrects; null = not linked (still counted). */
  correctsFactura: { id: string; label: string } | null;
}

/** G-121 S4: the Finances "Purchases & IVA paid" card for one month (owner only). */
export interface PurchasesIvaSummary {
  year: number;
  month: number;
  locationId: string | null;
  /**
   * `fromFacturasEur` + `typedByHandEur` = `totalEur`. G-164 option C (owner
   * 2026-10-19): the typed part is `typedNoDeliveryEur` (receives with no
   * delivery) + `notDeductibleEur` (waits closed this month). Typed IVA still
   * waiting for a factura is `waitingNotAddedEur` — shown beside, never added.
   */
  ivaPaid: {
    /** Facturas + credit notes (negative): the deductible figure. */
    fromFacturasEur: string;
    /** G-173: of `fromFacturasEur`, the credit notes' part (≤ 0). */
    creditNotesEur: string;
    typedByHandEur: string;
    typedNoDeliveryEur: string;
    notDeductibleEur: string;
    totalEur: string;
    waitingNotAddedEur: string;
  };
  /** Before IVA, facturas + typed by hand; and what the owed list adds up to. */
  totals: { purchasesNetEur: string; owedEur: string };
  facturas: CountedMoney & {
    incompleteCount: number;
    /** Header IVA − Σ printed per-rate IVA; not "0.00" when a paper's rows disagree with its total. */
    rateTableDifferenceEur: string;
    rows: PurchaseFacturaRow[];
  };
  /** G-173: credit notes counted this month, their figures negative. */
  creditNotes: CountedMoney & { rows: PurchaseCreditNoteRow[] };
  /** G-164 option C: the typed purchases in the total (no-delivery receives + waits closed this month). */
  typed: CountedMoney & {
    missingLines: CountedMoney;
    /** Received this month, still waiting for a factura — not in the total. */
    waiting: CountedMoney;
    /** Waits closed this month: no factura will come — IVA not deductible. */
    noFactura: CountedMoney;
    /** Received this month with no delivery — nothing can link them. */
    noDelivery: CountedMoney;
    /** G-131: typed purchases a factura covers — counted by the factura, not here. */
    coveredByFactura: { count: number; netEur: string; ivaEur: string };
    /** G-164: of `waiting`, purchases linked to a factura the card cannot count. */
    underUncountedFactura: CountedMoney;
  };
  byRate: Array<{
    ratePercent: string | null;
    facturas: { baseEur: string; ivaEur: string };
    typed: { netEur: string; ivaEur: string };
    ivaEur: string;
  }>;
  bySupplier: Array<{
    supplierId: string | null;
    name: string;
    facturas: CountedMoney;
    typed: CountedMoney;
    netEur: string;
    ivaEur: string;
  }>;
  byShop: Array<{
    locationId: string;
    name: string;
    code: string;
    facturas: CountedMoney;
    typed: CountedMoney;
    netEur: string;
    ivaEur: string;
  }>;
  typedByCategory: Array<CountedMoney & { categoryId: string; label: string }>;
  unknownBasis: { count: number; netEur: string };
  owed: Array<
    PurchasePaperRef & {
      dueDate: string | null;
      grandTotalEur: string | null;
      /** G-173: what its credit notes take off (≤ 0). */
      creditedEur: string | null;
      /** G-173: total + credited — what owed is measured against. */
      toPayEur: string | null;
      paidEur: string;
      owedEur: string | null;
      state: "UNPAID" | "PART_PAID" | "TOTAL_NOT_READ";
    }
  >;
  notCounted: {
    albaranes: { count: number; baseEur: string; rows: PurchasePaperRef[] };
    /** G-131: every albarán and hand-made delivery with no factura yet — any month. */
    waitingForFactura: { albaranes: number; deliveries: number };
    itemsOnly: { count: number };
    copies: Array<PurchasePaperRef & { countedId: string }>;
    notRead: PurchasePaperRef[];
    missingDateOrNumber: PurchasePaperRef[];
    ivaNotRead: PurchasePaperRef[];
    /** G-173: a factura with a negative figure, or a credit note with one above zero. */
    wrongSign: PurchasePaperRef[];
    /** G-164: every read factura or credit note the card cannot count — any month, copies left out — and why. */
    facturasToFix: Array<
      PurchasePaperRef & { reason: "NO_DATE_OR_NUMBER" | "IVA_NOT_READ" | "CREDIT_NOTE" | "CREDIT_NOTE_NOT_NEGATIVE" }
    >;
  };
}

export interface InvoiceDetail {
  id: string;
  locationId: string;
  supplierId: string;
  createdByUserId: string;
  originalFilename: string;
  storedPath: string;
  mimeType: string;
  sha256: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  currency: string;
  status: InvoiceWorkflowStatus;
  parserSupplierCode: string | null;
  parserVersion: string | null;
  parsedPayload: unknown;
  parseError: string | null;
  /**
   * G-121 S1: the document's money as the paper prints it, BESIDE the net
   * line figures (which stay the cost of goods). Each null = not known.
   * `dueDate` comes over the wire the way `invoiceDate` does.
   */
  baseEur: string | null;
  ivaEur: string | null;
  grandTotalEur: string | null;
  dueDate: string | null;
  moneySource: InvoiceMoneySource | null;
  vatBreakdown: InvoiceVatBreakdownRow[];
  /** G-121 S4 (OD-143): what the paper is for, said at upload; who said it last (null = the S4 migration). */
  paperUse: PaperUse;
  paperUseSetAt: string;
  paperUseSetByUserId: string | null;
  /** G-131: an albarán — the factura that counts it; a factura — "the goods came with it, no albarán". */
  coveredByFacturaId: string | null;
  coversNoAlbaranAt: string | null;
  createdAt: string;
  updatedAt: string;
  supplier: { id: string; code: string; name: string };
  location: { id: string; name: string; code: string };
  createdBy: { id: string; fullName: string };
  lines: InvoiceLineRow[];
  scheduledDelivery?: { id: string; status: DeliveryStatus } | null;
  /** G-164: whether the Finances card can count this paper, and if not why (copies aside). Sent by GET and every edit. */
  countability?: PaperCountability;
}

/** G-121 S4 / G-164: why a paper is, or is not, in the IVA-paid total (`paper-rules.ts` `paperCountability`). */
export type PaperCountability =
  | "COUNTED"
  | "NOT_A_BILL"
  | "NOT_READ"
  /** A factura with a negative figure: a credit note filed as the wrong type. */
  | "CREDIT_NOTE"
  /** G-173: a credit note with a figure above zero. */
  | "CREDIT_NOTE_NOT_NEGATIVE"
  | "NO_DATE_OR_NUMBER"
  | "IVA_NOT_READ";

export interface InvoicePreviewDetail extends Omit<InvoiceDetail, "lines"> {
  /** G-129: items the app created from this paper by itself, not undone. */
  autoCreatedCount: number;
  lines: InvoicePreviewLine[];
  /** G-131: what this paper covers, and why "Schedule delivery" may be refused. */
  coverage: InvoiceCoverage;
}

/** G-131: one albarán or hand-made delivery, as a factura covers it or could. */
export interface CoverageRow {
  kind: "albaran" | "delivery";
  id: string;
  label: string;
  date: string | null;
  supplier: { id: string; name: string };
  location: { id: string; name: string; code: string };
  /** Albarán: its printed base; delivery: the typed lines before IVA. */
  totalEur: string | null;
  broughtStock: boolean;
  coveredAt: string | null;
}

/** G-131: GET /invoices/:id/coverage — the server says every sentence. */
export interface InvoiceCoverage {
  covers: CoverageRow[];
  /** Σ the covered rows' totals before IVA, added on the server; rows with no total counted apart. */
  coversTotal: { eur: string; withoutTotal: number };
  /** Same supplier, waiting, dated on or before this factura, in the shops you see — only rows a Link would accept. */
  candidates: CoverageRow[];
  /** Waiting papers of this supplier in shops you cannot see. */
  waitingElsewhere: number;
  /** An albarán: the factura that counts it. */
  coveredBy: { id: string; label: string } | null;
  coversNoAlbaran: { at: string; byUserId: string | null } | null;
  scheduleBlock: { reason: string } | null;
}

/** G-131: GET /invoices/waiting-factura — oldest wait first. */
export interface WaitingForFacturaList {
  count: number;
  albaranes: number;
  deliveries: number;
  /** G-137: `noFacturaBlockedBy` — why "No factura will come" is refused for this row right now (no document), or null. */
  rows: Array<CoverageRow & { daysWaiting: number; noFacturaBlockedBy: string | null }>;
}

/** G-131: one row of the Supplier papers page (GET /invoices). */
export interface PaperListRow {
  id: string;
  label: string;
  invoiceNumber: string | null;
  originalFilename: string;
  invoiceDate: string | null;
  uploadedAt: string;
  uploadedBy: { id: string; fullName: string };
  supplier: { id: string; name: string };
  location: { id: string; name: string; code: string };
  paperUse: PaperUse;
  status: InvoiceWorkflowStatus;
  baseEur: string | null;
  ivaEur: string | null;
  grandTotalEur: string | null;
  delivery: { id: string; status: DeliveryStatus } | null;
  coveredByFactura: { id: string; label: string } | null;
  covers: number;
  /** G-173: a credit note — the factura it corrects, when linked and in a shop you see. */
  correctsFactura: { id: string; label: string } | null;
  /** G-173: linked to a factura of a shop you cannot see. */
  correctsElsewhere: boolean;
  /** G-173: a factura — how many credit notes are linked to it. */
  creditNotes: number;
  waitingForFactura: boolean;
  /** G-164: whether the Finances card can count this paper, and if not why (copies aside). */
  countability: PaperCountability;
}

/** G-173: one paper on the other side of a credit link. Figures as stored (a credit note's negative). */
export interface CreditRow {
  id: string;
  label: string;
  date: string | null;
  location: { id: string; name: string; code: string };
  baseEur: string | null;
  ivaEur: string | null;
  grandTotalEur: string | null;
  linkedAt: string | null;
}

/** G-173: a factura's three figures. Null = unknown. */
export interface CreditFigures {
  billedEur: string | null;
  creditedEur: string | null;
  toPayEur: string | null;
}

/** G-173: GET /invoices/:id/credits — a factura's credit notes, or the factura a credit note corrects. */
export type PaperCredits =
  | ({
      kind: "factura";
      /** Linked credit notes in your shops; the figures count every one. */
      creditNotes: CreditRow[];
      creditNotesElsewhere: number;
      candidates: CreditRow[];
      elsewhere: number;
    } & CreditFigures)
  | {
      kind: "creditNote";
      corrects: (CreditRow & CreditFigures) | null;
      /** Linked to a factura of a shop you cannot see. */
      correctsElsewhere: boolean;
      candidates: Array<CreditRow & CreditFigures>;
    }
  | { kind: "none" };

/** At most 200 rows, newest upload first; `total` is how many match. */
export interface PaperList {
  total: number;
  rows: PaperListRow[];
}

export interface ListPapersQuery {
  locationId?: string;
  supplierId?: string;
  status?: InvoiceWorkflowStatus;
  paperUse?: PaperUse;
  /** "YYYY-MM" */
  month?: string;
  search?: string;
}

export interface PatchInvoiceLinePayload {
  lineId: string;
  itemId?: string | null;
  zoneId?: string | null;
}

export interface CommitInvoicePayload {
  scheduledFor: string;
  defaultZoneId?: string | null;
}

export interface CreateScheduledDeliveryLinePayload {
  itemId: string;
  zoneId: string;
  expectedQty: number;
  expectedUnitCostEur: number;
  /** G-121 S3: required — which side of IVA the unit cost is on. */
  moneyBasis: TypedMoneyBasis;
}

export interface CreateScheduledDeliveryPayload {
  supplierId: string;
  locationId: string;
  scheduledFor: string;
  invoiceNumber?: string;
  notes?: string;
  lines: CreateScheduledDeliveryLinePayload[];
}

export interface UpdateScheduledDeliveryPayload {
  supplierId?: string;
  scheduledFor?: string;
  invoiceNumber?: string | null;
  notes?: string | null;
  lines?: CreateScheduledDeliveryLinePayload[];
}

export interface ConfirmDeliveryLinePayload {
  asExpected?: boolean;
  receivedQty?: number;
  /**
   * G-121 S2: of the received units, how many arrived damaged
   * (0 ≤ damagedQty ≤ receivedQty). Still stock; the server requires a photo.
   */
  damagedQty?: number;
  notes?: string;
  /** G-59: how the line was found. */
  foundBy?: DeliveryLineFoundBy;
}

/** G-59: barcode capture at first receipt. */
export interface CaptureDeliveryLineBarcodePayload {
  barcode: string;
}

export interface ListDeliveriesQuery {
  locationId?: string;
  supplierId?: string;
  status?: DeliveryStatus;
  from?: string;
  to?: string;
}

export type BlogPostStatus =
  | "NEW"
  | "GENERATING"
  | "DRAFT_READY"
  | "EDITING"
  | "PUBLISHING"
  | "POSTED"
  | "FAILED";

export type BlogPostAction = "NONE" | "EDITING" | "PUBLISH";

export type BlogPostTone = "LUXURY" | "WARM" | "EDITORIAL" | "PLAYFUL";

export type BlogPostLanguage = "en" | "es" | "ca" | "ru" | "uk";

export type BlogPostRegion = "ES" | "ES-CT" | "LATAM" | "UA" | "EU" | "WORLD";

export interface BlogPostRunLogEntry {
  id: string;
  blogPostId: string;
  fromState: string;
  toState: string;
  actor: string;
  details: string | null;
  costUsd: string;
  createdAt: string;
}

export interface BlogPostSummary {
  id: string;
  idea: string;
  language: BlogPostLanguage;
  region: BlogPostRegion;
  tone: BlogPostTone;
  targetWordCount: number;
  internalLinks: string;
  status: BlogPostStatus;
  action: BlogPostAction;
  lastEvent: string | null;
  errorMessage: string | null;
  shopifyArticleUrl: string | null;
  draftMarkdown: string;
  editCount: number;
  totalCostUsd: string;
  postedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; fullName: string; email: string };
}

export interface BlogPostResearchSource {
  url: string;
  title?: string | null;
  domain?: string | null;
  snippet?: string | null;
  rank?: number | null;
}

export interface BlogPostStyleGuardFix {
  pattern: string;
  count: number;
}

export interface BlogPostFaqItem {
  question: string;
  answer: string;
}

export interface BlogPostDetail extends BlogPostSummary {
  searchQueries: string[] | null;
  researchSources: BlogPostResearchSource[] | null;
  /** Written by the retired Gemini writer; kept for old rows. New rows carry `briefMarkdown`. */
  outlineMarkdown: string | null;
  styleGuardFixes: BlogPostStyleGuardFix[] | null;
  editNotes: string | null;
  heroImageUrl: string | null;
  heroImageKey: string | null;
  /** G-82: the managed agent's brief and explicit SEO fields. */
  briefMarkdown: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  slug: string | null;
  excerpt: string | null;
  heroAlt: string | null;
  tags: string[];
  faq: BlogPostFaqItem[] | null;
  agentSessionId: string | null;
  runLog: BlogPostRunLogEntry[];
}

export interface CreateBlogPostPayload {
  idea: string;
  language: BlogPostLanguage;
  /** Optional since G-82 (translation happens in Shopify); the server defaults it. */
  region?: BlogPostRegion;
  /** Optional since G-82: the writer finds catalog links itself when empty. */
  internalLinks?: string;
  tone?: BlogPostTone;
  targetWordCount?: number;
}

export interface TicketUserRef {
  id: string;
  fullName: string;
}

/**
 * G-72: a bug reported from a shop, as the owner's list receives it.
 *
 * `message` is EMPTY for an AUTOMATIC report — the error boundary files those
 * with nobody typing, which is how a crash nobody bothered to report still
 * reaches the owner.
 */
export interface BugReportListItem {
  id: string;
  kind: "REPORTED" | "AUTOMATIC";
  message: string;
  screen: string | null;
  consoleLog: string | null;
  errorMessage: string | null;
  errorStack: string | null;
  /** Null when the device could not take one, or it was filed from a browser. */
  screenshotPath: string | null;
  appVersion: string | null;
  userAgent: string | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  /** When it happened on the DEVICE — a queued report arrives much later. */
  occurredAt: string;
  createdAt: string;
  /** Server-set. The row and its screenshot are deleted once this passes. */
  expiresAt: string;
  resolvedAt: string | null;
  reportedBy: { id: string; fullName: string } | null;
  resolvedBy: { id: string; fullName: string } | null;
  location: { id: string; name: string } | null;
}

/** List row from GET /tickets */
export interface AppTicketListItem {
  id: string;
  title: string;
  description: string;
  status: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy: TicketUserRef;
  _count: { comments: number };
}

export interface TicketComment {
  id: string;
  ticketId: string;
  content: string;
  createdById: string;
  createdAt: string;
  createdBy: TicketUserRef;
}

/** GET /tickets/:id */
export interface AppTicketDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy: TicketUserRef;
  comments: TicketComment[];
}

export interface CreateTicketPayload {
  title: string;
  description: string;
}

export interface PatchTicketPayload {
  title?: string;
  description?: string;
  status?: string;
}

export interface PayrollSummaryRow {
  userId: string;
  fullName: string;
  role: Role;
  /** G-163 (D-78): false for a person who left — listed in their months and while a carried balance remains. */
  isActive: boolean;
  hoursWorked: string;
  hourlyRateEur: string | null;
  wageOwedEur: string;
  /** WO-B (OD-198): an hourly person worked hours with no rate anywhere — the wage counts them at €0. */
  missingHourlyRate: boolean;
  /** WO-B: those hours ("0.00" when none). */
  unratedHours: string;
  rateBreakdown: Array<{ rateEur: string | null; hours: string; wageEur: string }>;
  salesCommissionPercent: string | null;
  commissionOwedEur: string;
  /** Sum of BONUS adjustments for the period. Bonuses are auto-paid at issuance: they add to BOTH totalOwed AND totalPaid (net-zero on balance). */
  bonusEur: string;
  /** Sum of DEDUCTION adjustments for the period (decimal string; subtracts from totalOwed). */
  deductionEur: string;
  /** Under one shop: what this person cost that shop (G-126, OD-152). */
  totalOwedEur: string;
  /** null under one shop (G-126, OD-152): a payment pays a person, not a shop — All shops only. */
  totalPaidEur: string | null;
  /** null under one shop, like `totalPaidEur`. */
  balanceDueEur: string | null;
  /**
   * G-103 S2 (OD-82): null = hourly; set = salaried (hours are presence, not
   * pay). G-163: the salary in force in the window's last month.
   */
  monthlySalaryEur: string | null;
  /**
   * G-163 (KI-106): each month's salary in force that month, summed (0.00 for
   * hourly months); under one shop, only the months whose home shop it is.
   */
  salaryOwedEur: string;
  /**
   * OD-285: the salary's COST over the window — a share per calendar day of
   * each month it touches (a whole month = `salaryOwedEur`; "Today" = one day).
   */
  salaryCostEur: string;
  /** OD-285: wage + salaryCost + commission + bonus − deduction — Analytics' per-person total. */
  periodCostEur: string;
  /** G-163 (KI-107): what rates on a salaried month's shifts would have paid — not counted. */
  shiftRatesNotPaidEur: string;
  /** Under one shop: the home shop where this person's salary, bonuses or deductions are counted instead. */
  salaryCountedAt: { id: string; name: string } | null;
  /** G-103 S2 (D-32): shifts still IN_PROGRESS in the window — they pay nothing until ended. */
  inProgressShiftCount: number;
  /** OD-84: 1–31. */
  payDayOfMonth: number;
  /** "YYYY-MM-DD" — next pay day on or after today (Madrid). */
  nextPayDay: string;
  /**
   * G-163: owed − paid over the months from the server's carry-forward start
   * to the month before the window (negative = credit). null = not stated.
   * Shown as "Earlier months" on Finances.
   */
  unpaidEarlierEur: string | null;
  /** G-163 (owner 2026-10-19): the "Balance" column — this window + earlier months; null under one shop. */
  balanceWithEarlierEur: string | null;
}

/** G-163: GET /finances/payroll/pay-day-due — the owner's "Pay day passed" reminder. The app never pays anyone. */
export interface PayDayDue {
  people: number;
  owedEur: string;
  forYear: number;
  forMonth: number;
}

/**
 * G-108 (OD-79) — a payroll row as a non-owner receives it: hours, no money.
 * The server builds it from an allowlist (`toHoursRow`), so this is the whole
 * shape, not a row with fields missing.
 */
export type PayrollHoursRow = Pick<
  PayrollSummaryRow,
  | "userId"
  | "fullName"
  | "role"
  | "isActive"
  | "hoursWorked"
  | "inProgressShiftCount"
  | "payDayOfMonth"
  | "nextPayDay"
>;

interface PayrollSummaryMeta {
  /** null in range mode (D-33): a range has no bookkeeping period. */
  periodYear: number | null;
  periodMonth: number | null;
  viewMode: "month" | "range";
  rangeFrom: string | null;
  rangeTo: string | null;
  locationScope: "manager_shop" | "single_shop" | "all_shops";
  locationId: string | null;
  caveat: string;
}

/**
 * G-126 (OD-152), under one shop: money of the listed people who hold no home
 * shop (cleaners) — counted under All shops only. null under All shops.
 */
export interface PayrollNotSplitByShop {
  people: number;
  salaryEur: string;
  /** OD-285: the salary's daily-share cost in months with no home shop. */
  salaryCostEur: string;
  bonusEur: string;
  deductionEur: string;
}

/**
 * G-169 (WO-D): the window's staff cost, added once by the server from the
 * rows. `staffCostExcludingCommissionEur` (wage + salaryCost + bonus − deduction; OD-285)
 * is what the Analytics "Staff cost" tile shows: the commission is in the
 * margin chain already, so it is not counted a second time here (KI-116).
 */
export interface PayrollSummaryTotals {
  wageEur: string;
  /** Owed: the full month per month whose 1st is in the window (OD-82). */
  salaryEur: string;
  /** OD-285: the salary's share per calendar day of the window — what the tile counts. */
  salaryCostEur: string;
  commissionEur: string;
  bonusEur: string;
  deductionEur: string;
  totalOwedEur: string;
  staffCostExcludingCommissionEur: string;
}

/** `payVisible` is the server's decision (owner/admin only), not the page's. */
export type PayrollSummaryResponse = PayrollSummaryMeta &
  (
    | {
        payVisible: true;
        rows: PayrollSummaryRow[];
        notSplitByShop: PayrollNotSplitByShop | null;
        totals: PayrollSummaryTotals;
      }
    | { payVisible: false; rows: PayrollHoursRow[] }
  );

/** G-169: a person's adjustments for a month, with the month's sums made once by the server. */
export interface PayrollAdjustmentsResponse {
  rows: PayrollAdjustmentRecord[];
  totals: { bonusEur: string; deductionEur: string; netEur: string };
}

/** G-169: the listed deliveries' figures (`GET /scheduled-deliveries/totals`), added once by the server. */
export interface DeliveryListTotals {
  expectedEur: string;
  /** Non-null only when every listed delivery has a billed total. */
  billedEur: string | null;
  receivedEur: string;
  shortageEur: string;
  overageEur: string;
  confirmedLines: number;
  totalLines: number;
  deliveries: number;
  /** At least one listed delivery came from an invoice. */
  anyInvoice: boolean;
}

/** G-126 review: what a person has been paid for one payroll month, by the server's one rule (payments + bonuses). */
export interface PayrollPaidMonth {
  paymentsEur: string;
  bonusesEur: string;
  paidEur: string;
}

export interface PayrollPaymentRecord {
  id: string;
  amountEur: string;
  paymentType: "CASH" | "BANK";
  paidAt: string;
  notes: string | null;
  createdAt: string;
  recordedBy: { id: string; fullName: string };
}

export interface CreatePayrollPaymentPayload {
  userId: string;
  periodYear: number;
  periodMonth: number;
  amountEur: number;
  paymentType: "CASH" | "BANK";
  paidAt?: string;
  notes?: string;
}

export type PayrollAdjustmentKind = "BONUS" | "DEDUCTION";

export interface PayrollAdjustmentRecord {
  id: string;
  kind: PayrollAdjustmentKind;
  amountEur: string;
  reason: string;
  periodYear: number;
  periodMonth: number;
  occurredAt: string;
  hasImage: boolean;
  imageMimeType: string | null;
  createdAt: string;
  recordedBy: { id: string; fullName: string };
}

export interface CreatePayrollAdjustmentPayload {
  kind: PayrollAdjustmentKind;
  userId: string;
  amountEur: number;
  reason: string;
  periodYear: number;
  periodMonth: number;
  occurredAt?: string;
  /** Optional evidence photo (JPEG/PNG/WEBP, ≤5MB). */
  image?: File;
}

// ── Catalog drafts (invoice → staging workspace → catalog) ───────────────────

export type CatalogDraftStatus = "PENDING" | "FINALIZED";

export interface CatalogDraft {
  id: string;
  supplierId: string;
  sourceInvoiceId: string | null;
  supplierSku: string | null;
  barcodeRaw: string | null;
  nameRaw: string;
  name: string;
  producer: string | null;
  categoryId: string | null;
  defaultUom: DefaultUom | null;
  colorId: string | null;
  stemHeightCm: string | null;
  /**
   * G-45 follow-up: OPTIONAL retail price. Null means the promoted item is
   * priceless by design and its price is derived from real batch cost.
   */
  defaultSaleUnitPriceEur: string | null;
  diameterCm: string | null;
  depthCm: string | null;
  subCategoryId: string | null;
  isPerishable: boolean;
  imageKey: string | null;
  /**
   * G-58: what the supplier PDF carried beyond text. The thumbnail is a 64 px
   * preview for the review pass, never the item photo; the barcode picture is
   * shown when the digits could not be read.
   */
  supplierThumbnailKey: string | null;
  barcodeImageKey: string | null;
  /** G-58: the parser's category guess (an ItemCategory code) — a suggestion only. */
  suggestedCategoryCode: string | null;
  /**
   * G-58 follow-up: the colour named in the last two words of the supplier
   * description (a Color name) — a suggestion, applied only where blank.
   */
  suggestedColorName: string | null;
  /**
   * G-66 follow-up: the flower TYPE the description names (an
   * ItemSubCategory code) — a suggestion, applied only where blank and only
   * when it belongs to the category being applied.
   */
  suggestedSubCategoryCode: string | null;
  /** G-58: the invoice unit cost the draft came from. */
  sourceUnitCostEur: string | null;
  /**
   * G-58: what the item will sell for once received — the G-45 target price
   * from the invoice cost and the chosen (else suggested) category's margin.
   * Null when there is nothing to price with.
   */
  suggestedSalePriceEur: string | null;
  /**
   * G-129: the active items that already carry this draft's name — why the
   * automatic step left it pending; pick one as the existing item, or create
   * anyway.
   */
  nameTwins: ExistingItemRef[];
  draftGroupId: string | null;
  status: CatalogDraftStatus;
  finalizedItemId: string | null;
  createdAt: string;
  updatedAt: string;
  supplier: { id: string; code: string; name: string };
  category: { id: string; code: string; label: string } | null;
  color: { id: string; name: string; hex: string | null } | null;
  subCategory: SubCategory | null;
  group: { id: string; name: string } | null;
  sourceInvoice: { id: string; invoiceNumber: string | null } | null;
}

export interface CatalogDraftGroup {
  id: string;
  name: string;
  categoryId: string | null;
  createdAt: string;
  updatedAt: string;
  category: { id: string; code: string; label: string } | null;
  _count: { drafts: number };
}

export interface CatalogDraftWorkspace {
  drafts: CatalogDraft[];
  groups: CatalogDraftGroup[];
}

/** G-129 (OD-160): one item the app created by itself from a paper (GET /catalog-drafts/auto-created). */
export interface AutoCreatedItemRow {
  auditLogId: string;
  createdAt: string;
  item: {
    id: string;
    name: string;
    imageKey: string | null;
    category: { id: string; code: string; label: string };
  };
  supplier: { id: string; name: string } | null;
  /** Null once the paper was deleted (G-127). */
  paper: { id: string; invoiceNumber: string | null; originalFilename: string } | null;
  nameRaw: string | null;
}

/** G-58: one review pass — promote many drafts with the suggestions applied. */
export interface FinalizeManyDraftsPayload {
  draftIds: string[];
  applySuggestions?: boolean;
  /** G-119: the ids of the twins shown for drafts in this pass, promoted past anyway. */
  confirmDuplicateOf?: string[];
}

/** G-119: an active item that already carries a name being created. */
export interface ExistingItemRef {
  id: string;
  name: string;
}

export interface FinalizeManyDraftsResult {
  results: Array<{
    draftId: string;
    itemId: string | null;
    error: string | null;
    /** G-119: set when the draft was held back because these items carry its name. */
    duplicateOf?: ExistingItemRef[];
  }>;
  promoted: number;
  failed: number;
}

export interface UpdateDraftPayload {
  name?: string;
  producer?: string | null;
  supplierSku?: string | null;
  barcodeRaw?: string | null;
  categoryId?: string | null;
  defaultUom?: DefaultUom | null;
  colorId?: string | null;
  subCategoryId?: string | null;
  stemHeightCm?: number | null;
  /** G-45 follow-up: optional; null leaves the price to be derived from cost. */
  defaultSaleUnitPriceEur?: number | null;
  diameterCm?: number | null;
  depthCm?: number | null;
  isPerishable?: boolean;
  draftGroupId?: string | null;
}

export interface CreateDraftGroupPayload {
  name: string;
  categoryId?: string | null;
  draftIds?: string[];
}

export interface UpdateDraftGroupPayload {
  name?: string;
  categoryId?: string | null;
}

// ── Customer orders (scheduled orders + calendar) ───────────────────────────

export type OrderSource =
  | "MANUAL"
  | "PHONE"
  | "WHATSAPP"
  | "INSTAGRAM"
  | "SHOPIFY"
  | "GLOVO"
  | "API"
  | "EMAIL";

/** G-56: the sources a person may pick on Build & send (MANUAL = "Other"). */
export type StaffOrderSource = Extract<
  OrderSource,
  "MANUAL" | "PHONE" | "WHATSAPP" | "INSTAGRAM"
>;
export type OrderFulfillment = "PICKUP" | "COURIER" | "EXTERNAL_COURIER";
export type CustomerOrderStatus =
  | "NEW"
  | "CONFIRMED"
  | "IN_PREPARATION"
  | "READY"
  | "COMPLETED"
  | "CANCELLED";
export type DeliveryTimeType = "ASAP" | "EXACT" | "WINDOW";
export type CustomerOrderLineRole =
  | "GENERAL"
  | "BOUQUET_COMPONENT"
  | "RETAIL_ITEM"
  /** G-112: a finished bouquet from stock the order holds. */
  | "READY_BOUQUET";

export interface CustomerOrderLine {
  id: string;
  description: string;
  itemId: string | null;
  itemName: string | null;
  bouquetTemplateId: string | null;
  bouquetTemplateName: string | null;
  quantity: string;
  unitPriceEur: string | null;
  role: CustomerOrderLineRole;
  pickedAt: string | null;
  pickedBy: { id: string; fullName: string } | null;
  /** G-112: the bouquet a READY_BOUQUET line holds (null for other roles). */
  bouquetBuild: { id: string; name: string | null; status: string } | null;
}

/**
 * An order payment's method. ONLINE (G-152, owner 2026-10-14) is money paid
 * online — what a web customer paid Shopify (the server's poll), or a linked
 * office sale paid online. No picker offers it.
 */
export type OrderPaymentMethod = "CASH" | "CARD" | "BANK_TRANSFER" | "ONLINE";
/** What a person may record: every method except the poll's ONLINE. */
export type StaffOrderPaymentMethod = Exclude<OrderPaymentMethod, "ONLINE">;
/** G-162: REFUND_REMOVED answers a removed sale refund's REFUND row (it adds back). */
export type OrderPaymentKind = "DEPOSIT" | "BALANCE" | "REFUND" | "REFUND_REMOVED";
/** What a person records with "Record payment". */
export type RecordableOrderPaymentKind = Exclude<OrderPaymentKind, "REFUND_REMOVED">;

/** OD-30: record the buyer's money on an order (couriers never take payment). */
export interface RecordOrderPaymentPayload {
  kind: RecordableOrderPaymentKind;
  method: StaffOrderPaymentMethod;
  amountEur: number;
  notes?: string;
}

/** G-22: per-source commission toggle (owner-only). Absence of a rule = OFF. */
export interface CommissionRuleView {
  source: OrderSource;
  commissionEnabled: boolean;
  updatedBy: { id: string; fullName: string } | null;
  updatedAt: string | null;
}

/** G-20: one row of the order's append-only money ledger. */
export interface OrderPaymentView {
  id: string;
  kind: OrderPaymentKind;
  /** Null only on rows backfilled from the legacy prepaid flag. */
  method: OrderPaymentMethod | null;
  amountEur: string;
  takenAt: string;
  takenBy: { id: string; fullName: string } | null;
  context: "CRM" | "FLOOR" | "COURIER";
  notes: string | null;
}

/**
 * G-152: what Shopify charged for a web order. The money facts are null for a
 * web order stored before G-152 (the honest unknown). `shippingEur` is the
 * order's paid delivery fee.
 */
/**
 * G-152 / KI-150: what Shopify charged for a web order and its payment
 * state — the server's `shopifyMoneyView`, one shape for the office order
 * window and the Shopify orders page. Null facts: stored before G-152.
 */
export interface ShopifyMoneyFacts {
  /** Shopify's total: goods + shipping (+ tax when prices exclude it). */
  totalEur: string;
  subtotalEur: string | null;
  shippingEur: string | null;
  totalTaxEur: string | null;
  taxesIncluded: boolean | null;
  totalDiscountsEur: string | null;
  /** Shopify's `financial_status`: paid, pending, authorized, partially_paid, … */
  financialStatus: string | null;
  paymentGatewayNames: string[];
  /** KI-173: when Shopify cancelled the order, whatever the CRM's status. */
  cancelledAt: string | null;
}

export interface CustomerOrderShopifyMoney extends ShopifyMoneyFacts {
  id: string;
  orderNumber: string | null;
}

export interface CustomerOrder {
  id: string;
  code: string;
  source: OrderSource;
  externalRef: string | null;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  customerNotes: string | null;
  bouquetName: string | null;
  bouquetNotes: string | null;
  orderType: string | null;
  fulfillment: OrderFulfillment;
  timeType: DeliveryTimeType;
  scheduledDate: string;
  scheduledFor: string | null;
  scheduledUntil: string | null;
  status: CustomerOrderStatus;
  /** Agreed GOODS total (bouquet + shop items); delivery lives on the job. */
  totalEur: string | null;
  /** G-15: agreed price of the bouquet itself. */
  bouquetPriceEur: string | null;
  prepaidAt: string | null;
  /**
   * G-166 (owner 2026-10-20): marked "already paid" but no money was ever
   * recorded — shown as "Prepaid · not recorded" until someone records it.
   */
  prepaidNotRecorded: boolean;
  /** G-177 (KI-89): the courier's job is DELIVERED, the order still open (its sale could not be written). */
  deliveredNotCompleted: boolean;
  /**
   * G-166 (owner 2026-10-20, KI-147): a web order's mirror no shop handed over
   * yet — only "Hand to delivery" may finish it; every other door is refused.
   */
  webAwaitsHandover: boolean;
  /**
   * G-166: handed over but not yet carried out — no courier job, a job
   * nobody carries, or a courier's tap the shop has not confirmed. Still
   * listed on the tablet and the calendar, still "needs a courier".
   */
  webAwaitingCourier: boolean;
  /** G-20: when the customer took the goods at the counter (PICKUP). */
  collectedAt: string | null;
  /** OD-32: reality diverged from the request (price charged / recipe built /
   *  linked-sale total) — the AMENDED timeline events hold the detail. */
  divergedFromRequest: boolean;
  /** G-20/OD-29: SUM of the payment rows — never a stored column. */
  paidEur: string;
  /** G-112 (OD-100): the courier fee owed through this order's ledger
   *  ("0.00" for pickup, a cancelled job, or a legacy walk-in order). G-152:
   *  a web order's fee is Shopify's shipping, whatever its job's price. */
  deliveryFeeEur: string;
  /** G-112: everything the customer owes — goods + that fee; null while no
   *  goods price is agreed. */
  amountOwedEur: string | null;
  /** amountOwedEur minus payments, never negative; null while unpriced. */
  balanceDueEur: string | null;
  /**
   * KI-104 (owner 2026-10-19): paid beyond what is owed — once handed over,
   * beyond what was charged + the fee. Accepted, never hidden. Null unpriced.
   */
  overpaidEur: string | null;
  /** KI-104: refunds beyond everything paid ("0.00" when none). */
  refundedBeyondPaidEur: string;
  payments: OrderPaymentView[];
  cancelReason: string | null;
  location: { id: string; name: string; code: string };
  createdBy: { id: string; fullName: string } | null;
  /** G-14: who started preparation; null for orders prepared pre-feature. */
  preparedBy: { id: string; fullName: string } | null;
  /** G-162: `refundedEur` — given back on the sale (removed refunds excluded); G-169: `refundableEur` — what is left to give back. */
  sale: { id: string; soldAt: string; totalEur: string; refundedEur: string; refundableEur: string } | null;
  shopifyOrder: CustomerOrderShopifyMoney | null;
  deliveryJob: {
    id: string;
    code: string;
    status: string;
    /** G-21 amendment: null until the shop confirms "picked up by courier". */
    handoverConfirmedAt: string | null;
    courier: { id: string; fullName: string } | null;
    /** The delivery fee (G-112: owed through the order's ledger). */
    priceEur: string;
  } | null;
  bouquetBuild: {
    id: string;
    name: string | null;
    status: string;
    createdAt: string;
  } | null;
  /** G-15: number of reference photos (was the single hasReferenceImage). */
  referencePhotoCount: number;
  /**
   * G-15: the SERVER's answer to "does this still need a floor bouquet
   * build?" — never re-derive it client-side, it must not drift from the
   * READY gate.
   */
  bouquetRequired: boolean;
  lines: CustomerOrderLine[];
  createdAt: string;
  updatedAt: string;
}

/** G-16: one photo of a finished build, or the courier's delivery proof. */
export interface BouquetBuildPhotoEntry {
  /** Null for the DELIVERY_PROOF pseudo-entry. */
  id: string | null;
  origin: "BUILD" | "DELIVERY_PROOF";
  deliveryJobId?: string;
  mimeType: string | null;
  uploadedBy: { id: string; fullName: string } | null;
  createdAt: string | null;
}

/** G-16: one build's recipe. Money fields are ABSENT for florists. */
export interface BouquetBuildDetail {
  id: string;
  name: string | null;
  notes: string | null;
  status: string;
  createdAt: string;
  soldAt: string | null;
  location: { id: string; name: string; code: string };
  builtBy: { id: string; fullName: string } | null;
  template: { id: string; name: string } | null;
  lines: Array<{
    id: string;
    item: { id: string; name: string } | null;
    quantity: string;
    uom: string | null;
    saleUnitPriceEur?: string | null;
  }>;
  totalCogsEur?: string | null;
  /** WO-B (OD-198): components of unknown cost — the cost is then the known part. */
  unknownCostComponentCount?: number;
  suggestedSalePriceEur?: string | null;
  salePriceEur?: string | null;
}

/** G-16: one row of the order's event trail. */
export interface CustomerOrderEventView {
  id: string;
  type: string;
  actor: { id: string; fullName: string } | null;
  metadata: unknown;
  createdAt: string;
}

/** G-15: one of up to 10 reference photos sent with the order. */
export interface OrderReferencePhoto {
  id: string;
  mimeType: string;
  uploadedBy: { id: string; fullName: string } | null;
  createdAt: string;
}

export interface OrdersCalendarEntry {
  date: string;
  status: CustomerOrderStatus;
  fulfillment: OrderFulfillment;
  count: number;
}

export interface CustomerOrderLinePayload {
  description: string;
  itemId?: string;
  bouquetTemplateId?: string;
  /** G-112: the finished bouquet a READY_BOUQUET line holds. */
  bouquetBuildId?: string;
  quantity?: number;
  unitPriceEur?: number;
  role?: CustomerOrderLineRole;
}

export interface OrderableItem {
  id: string;
  name: string;
  imageKey: string | null;
  defaultUom: DefaultUom;
  /** The price a person typed on the item, or null if nobody did. */
  defaultSaleUnitPriceEur: string | null;
  /** What it sells for: typed, else derived from cost × margin (G-45). Render this. */
  saleUnitPriceEur: string | null;
  saleUnitPriceIsDerived: boolean;
  qtyOnHand: string;
  hasBarcode: boolean;
  category: { id: string; code: string; label: string };
  color: { id: string; name: string; hex: string | null } | null;
}

export interface CreateCustomerOrderPayload {
  locationId: string;
  source?: StaffOrderSource;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  customerNotes?: string;
  bouquetName?: string | null;
  bouquetNotes?: string | null;
  orderType?: string;
  fulfillment: OrderFulfillment;
  timeType?: DeliveryTimeType;
  scheduledDate: string;
  scheduledFor?: string;
  scheduledUntil?: string;
  totalEur?: number;
  bouquetPriceEur?: number;
  prepaid?: boolean;
  /** G-20: the deposit actually taken now; omitted with prepaid = paid in full. */
  prepaidAmountEur?: number;
  prepaidMethod?: StaffOrderPaymentMethod;
  confirmed?: boolean;
  lines: CustomerOrderLinePayload[];
}

export interface UpdateCustomerOrderPayload {
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerNotes?: string;
  bouquetName?: string | null;
  bouquetNotes?: string | null;
  orderType?: string;
  fulfillment?: OrderFulfillment;
  timeType?: DeliveryTimeType;
  scheduledDate?: string;
  scheduledFor?: string;
  scheduledUntil?: string;
  totalEur?: number;
  bouquetPriceEur?: number;
  lines?: CustomerOrderLinePayload[];
}

export interface ListCustomerOrdersQuery {
  locationId?: string;
  from?: string;
  to?: string;
  status?: CustomerOrderStatus;
  fulfillment?: OrderFulfillment;
  source?: OrderSource;
  /** G-14 history: DELIVERY groups COURIER + EXTERNAL_COURIER. */
  fulfillmentKind?: "PICKUP" | "DELIVERY";
  minTotalEur?: number;
  preparedByUserId?: string;
  courierUserId?: string;
  search?: string;
  /** Present take/skip = history mode: newest first, paginated. */
  take?: number;
  skip?: number;
}

// ── Delivery jobs & payout rules (dispatch) ─────────────────────────────────

export type DeliveryJobStatus =
  | "PENDING"
  | "ASSIGNED"
  | "PICKED_UP"
  | "DELIVERED"
  | "FAILED"
  | "CANCELLED";
export type PackageSize = "SMALL" | "MEDIUM" | "LARGE";
export type PayoutRuleKind = "PERCENT" | "FLAT" | "PER_KM";

/** G-4: an in-flight delivery whose courier flagged a problem. */
export interface DeliveryProblem {
  jobId: string;
  code: string;
  status: DeliveryJobStatus;
  courierName: string | null;
  message: string;
  reportedAt: string | null;
}

export interface DeliveryJob {
  id: string;
  code: string;
  status: DeliveryJobStatus;
  order: {
    id: string;
    code: string;
    status: CustomerOrderStatus;
    customerName: string;
    lines: Array<{ id: string; description: string; quantity: string }>;
    /** OD-100/101: goods + this job's fee still owed. The courier never
     *  collects it; pickup is refused until the shop records the payment. */
    balanceDueEur: string | null;
    /** G-166: a web order the shop has not handed over yet — pickup is refused. */
    webAwaitsHandover: boolean;
  } | null;
  location: { id: string; name: string; code: string };
  pickupAddress: string;
  pickupLat: string | null;
  pickupLng: string | null;
  deliveryAddress: string;
  deliveryLat: string | null;
  deliveryLng: string | null;
  recipientName: string;
  recipientPhone: string | null;
  recipientNotes: string | null;
  courier: {
    id: string;
    fullName: string;
    phoneNumber: string | null;
    vehicleType: VehicleType | null;
    vehiclePlate: string | null;
  } | null;
  assignedBy: { id: string; fullName: string } | null;
  assignedAt: string | null;
  acceptedAt: string | null;
  arrivedAtPickupAt: string | null;
  /** G-21 amendment (OD-28): the shop's "picked up by courier" witness. */
  handoverConfirmedAt: string | null;
  handoverConfirmedBy: { id: string; fullName: string } | null;
  scheduledDate: string;
  scheduledPickupAt: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  failureReason: string | null;
  packageSize: PackageSize | null;
  distanceKm: string | null;
  priceEur: string;
  courierPayoutEur: string | null;
  companyMarginEur: string | null;
  /** OD-100: true when the fee is paid through the order's ledger (then
   *  `paidByClient` is derived from it and "Client paid" is not offered). */
  feePaidThroughOrder: boolean;
  paidByClient: boolean;
  paidByClientAt: string | null;
  paidToCourier: boolean;
  paidToCourierAt: string | null;
  hasProofPhoto: boolean;
  createdBy: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryJobEvent {
  id: string;
  fromStatus: DeliveryJobStatus | null;
  toStatus: DeliveryJobStatus | null;
  note: string | null;
  /** WP-B: position captured at this action and its distance to the target pin. */
  lat: string | null;
  lng: string | null;
  distanceM: number | null;
  /** True when distanceM is beyond GEO_VERIFY_RADIUS_M — render as a warning. */
  geoFlagged: boolean;
  actor: { id: string; fullName: string } | null;
  createdAt: string;
}

export interface DeliveryJobDetail extends DeliveryJob {
  events: DeliveryJobEvent[];
}

/**
 * WP-B verification outcome returned by the courier's arrive/deliver taps.
 * `null` means no check could run — position not shared, or the target address
 * was never geocoded. Never present that case to the courier as a failed check.
 */
export type GeoCheck = {
  withinRadius: boolean;
  distanceM: number;
  radiusM: number;
} | null;

export interface CourierActionResult extends DeliveryJob {
  geoCheck: GeoCheck;
  /**
   * OD-4 arrival gate: false = the tap did NOT record arrival —
   * `arrivalBlocked` says why (out-of-radius needs the courier's explicit
   * override; no position needs the floor tablet's confirmation).
   */
  arrivalRecorded?: boolean;
  arrivalBlocked?: "OUT_OF_RADIUS" | "NO_POSITION";
}

export type NotificationKind =
  | "DELIVERY_JOB_ASSIGNED"
  | "DELIVERY_JOB_UNASSIGNED"
  | "DELIVERY_JOB_CANCELLED"
  | "COURIER_SHIFT_MISSED"
  | "COURIER_SHIFT_WITHDRAWN"
  | "STOCK_ADJUSTED"
  | "DELIVERY_PROBLEM_REPORTED"
  | "KIOSK_GEO_FLAGGED"
  | "KIOSK_NO_SHIFT_ACTION"
  | "SHIFT_LATE"
  | "SHIFT_MISSED"
  | "SHIFT_CLOCKOUT_FORGOTTEN"
  | "SHIFT_STARTED_EARLY_ALERT"
  | "BUG_REPORT_FILED"
  | "SHOPIFY_ORDER_CHANGED_AFTER_ACCEPT"
  | "SHOPIFY_ORDER_UNFULFILLABLE"
  | "SHOPIFY_ORDER_NOT_LINKED"
  | "SHOPIFY_ORDER_NOTHING_LINKED"
  | "SHOPIFY_ORDER_STOCK_TO_MOVE"
  | "SHOPIFY_ORDER_MULTI_BOUQUET_LINE"
  | "SHOPIFY_ORDER_WAITING"
  | "SHOPIFY_ORDER_DUE_NOT_BUILT"
  | "SHOPIFY_ORDER_BACK_IN_POOL"
  | "SHOPIFY_DELIVERY_FAILED"
  | "ORDER_RECIPE_CHANGED"
  | "ORDER_DELIVERED_NOT_COMPLETED";

/** Display snapshot stored on the notification when the event happened. */
export interface DeliveryJobNotificationPayload {
  jobId: string;
  jobCode: string;
  orderCode: string | null;
  carrying: string[];
  pickupAddress: string;
  deliveryAddress: string;
  recipientName: string;
  scheduledDate: string;
  scheduledPickupAt: string | null;
  payoutEur: string | null;
}

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /** A snapshot of the event; Shopify kinds carry `orderId` (G-160). */
  payload: (DeliveryJobNotificationPayload & { orderId?: string }) | null;
  deliveryJobId: string | null;
  readAt: string | null;
  createdAt: string;
}

/** G-2: a courier's planning day (CourierShift). */
export type CourierShiftStatus =
  | "PLANNED"
  | "CONFIRMED"
  | "ENDED"
  | "MISSED"
  | "CANCELLED";

export interface CourierShift {
  id: string;
  courierUserId: string;
  courier: { id: string; fullName: string };
  /** YYYY-MM-DD. */
  date: string;
  plannedStart: string | null;
  plannedEnd: string | null;
  status: CourierShiftStatus;
  confirmedAt: string | null;
  endedAt: string | null;
  notes: string | null;
  createdByUserId: string;
  /** True when the day was planned by an owner, not the courier. */
  ownerCreated: boolean;
}

/** Today's plan as dispatch sees it (null = not scheduled today). */
export interface CourierTodayShift {
  status: CourierShiftStatus;
  plannedStart: string | null;
  plannedEnd: string | null;
}

/** §16.6: the courier's own payout rule, in full — deliberate asymmetry. */
export interface CourierPayoutRuleView {
  hasRule: boolean;
  rule: PayoutRule | null;
}

export interface AssignableCourier {
  id: string;
  fullName: string;
  phoneNumber: string | null;
  vehicleType: VehicleType | null;
  vehiclePlate: string | null;
  isAvailable: boolean;
  /** OD-5: vehicle carry cap (null = no limit) and live ASSIGNED+PICKED_UP count. */
  maxConcurrentJobs: number | null;
  activeJobCount: number;
  /** G-2: today's plan — drives the OD-2 "Assign anyway" soft-block. */
  todayShift: CourierTodayShift | null;
}

export interface CreateDeliveryJobPayload {
  orderId?: string;
  locationId?: string;
  deliveryAddress: string;
  pickupAddress?: string;
  recipientName?: string;
  recipientPhone?: string;
  recipientNotes?: string;
  scheduledDate?: string;
  scheduledPickupAt?: string;
  packageSize?: PackageSize;
  distanceKm?: number;
  priceEur: number;
}

export interface UpdateDeliveryJobPayload {
  pickupAddress?: string;
  deliveryAddress?: string;
  recipientName?: string;
  recipientPhone?: string;
  recipientNotes?: string;
  scheduledDate?: string;
  scheduledPickupAt?: string;
  packageSize?: PackageSize;
  distanceKm?: number;
  priceEur?: number;
}

export interface ListDeliveryJobsQuery {
  locationId?: string;
  from?: string;
  to?: string;
  status?: DeliveryJobStatus;
  courierUserId?: string;
}

export interface AssignmentPreview {
  hasRule: boolean;
  requiresDistance: boolean;
  payoutEur: string | null;
  marginEur: string | null;
}

export interface PreviewDeliveryPayoutPayload {
  locationId: string;
  courierUserId: string;
  priceEur: number;
  distanceKm?: number;
}

export interface PayoutRule {
  id: string;
  name: string;
  kind: PayoutRuleKind;
  percent: string | null;
  flatEur: string | null;
  ratePerKmEur: string | null;
  baseFeeEur: string | null;
  minPayoutEur: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** G-1: one row of the owner's Manage-couriers overview. */
export interface CourierOverviewRow {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string | null;
  isActive: boolean;
  vehicleType: VehicleType | null;
  vehiclePlate: string | null;
  isAvailable: boolean;
  notes: string | null;
  maxConcurrentJobs: number | null;
  activeJobCount: number;
  deliveredCount: number;
  unpaidPayoutEur: string;
  payoutRule: PayoutRule | null;
  /** G-2: today's plan for the schedule column (null = not scheduled). */
  todayShift: CourierTodayShift | null;
}

export interface CreatePayoutRulePayload {
  name: string;
  kind: PayoutRuleKind;
  percent?: number;
  flatEur?: number;
  ratePerKmEur?: number;
  baseFeeEur?: number;
  minPayoutEur?: number;
  isDefault?: boolean;
}

// ── Vacations (Finalizing.MD T3) ─────────────────────────────────────────────

export type VacationRequestStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED";

/** Account-plane security events (Prisma `AuthEvent`) — the Security tab. */
export type AuthEvent =
  | "LOGIN_SUCCESS"
  | "LOGIN_FAILURE"
  | "REFRESH"
  | "LOGOUT"
  | "LOCKED"
  | "PIN_SET"
  | "PIN_VIEWED"
  | "FOB_ISSUED"
  | "FOB_REVOKED"
  | "PASSWORD_RESET"
  // G-136 — emailed account links.
  | "INVITE_ISSUED"
  | "INVITE_CANCELLED"
  | "ACCOUNT_SETUP_COMPLETED"
  | "EMAIL_VERIFIED"
  | "EMAIL_CHANGE_REQUESTED"
  | "EMAIL_CHANGED"
  // G-149 — AI control: new instructions published, or an earlier version put back.
  | "AI_INSTRUCTIONS_PUBLISHED"
  | "AI_INSTRUCTIONS_ROLLED_BACK"
  // G-163 — payroll writes (owner-only, like pay itself).
  | "PAYROLL_PAYMENT_RECORDED"
  | "PAYROLL_PAYMENT_DELETED"
  | "PAYROLL_ADJUSTMENT_RECORDED"
  | "PAYROLL_ADJUSTMENT_DELETED"
  | "PAY_TERMS_CHANGED"
  // G-170 — pay rules: commission per order source, courier payout rules.
  | "COMMISSION_RULE_CHANGED"
  | "COURIER_PAYOUT_RULE_CREATED"
  | "COURIER_PAYOUT_RULE_CHANGED";

/** One row of GET /auth/audit-log (owner/admin only). */
export interface AuthAuditLogEntry {
  id: string;
  event: AuthEvent;
  createdAt: string;
  /** Null for a failed login against an unknown address, or a deleted user. */
  user: { id: string; fullName: string; role: Role } | null;
  /** The attempted address when there is no user — the row worth reading. */
  email: string | null;
  ip: string | null;
  userAgent: string | null;
  metadata: unknown;
}

export interface ListAuthAuditLogQuery {
  userId?: string;
  event?: AuthEvent;
  from?: string;
  to?: string;
  includeTokenActivity?: boolean;
  take?: number;
  skip?: number;
}

/** G-54: catalog change-log actions (Prisma `CatalogAuditAction`). */
export type CatalogAuditAction =
  | "ITEM_CREATED"
  | "ITEM_UPDATED"
  | "ITEM_ARCHIVED"
  | "ITEM_RESTORED"
  | "DRAFT_FINALIZED"
  | "TEMPLATE_CREATED"
  | "TEMPLATE_UPDATED"
  /** G-115: a row above was reversed from the log. */
  | "ACTION_UNDONE"
  /** G-127: an uploaded supplier paper was deleted (entityType "Invoice"). */
  | "PAPER_DELETED"
  /** G-131: a factura was linked to an albarán or hand-made delivery, or unlinked. */
  | "PAPER_LINKED"
  | "PAPER_UNLINKED"
  /** G-131 (D-86): a cancelled delivery gave its paper back. */
  | "PAPER_RELEASED"
  /** G-131: no factura will come for a delivery, or the goods came with the factura. */
  | "FACTURA_WAIT_DISMISSED"
  /** G-131: the receiving of one delivery line was undone. */
  | "DELIVERY_RECEIVE_UNDONE"
  /** G-137 (OD-184): a document was added to / deleted from a delivery typed by hand. */
  | "DELIVERY_DOCUMENT_ADDED"
  | "DELIVERY_DOCUMENT_DELETED"
  /** G-164: a person typed a factura's number or date. */
  | "PAPER_DETAILS_TYPED"
  /** G-164 (D-85): a delivery nothing was received from was moved to another shop. */
  | "DELIVERY_MOVED"
  /** G-164 (KI-165): the owner reopened a closed wait for a factura. */
  | "FACTURA_WAIT_REOPENED"
  /** G-170 (WO-E part 3): the money edits — a lot corrected, a category born or re-rated, a cost or expense written. */
  | "LOT_CORRECTED"
  | "CATEGORY_CREATED"
  | "CATEGORY_PRICING_CHANGED"
  | "RUNNING_COST_CREATED"
  | "RUNNING_COST_CHANGED"
  | "RUNNING_COST_DELETED"
  | "EXPENSE_CREATED"
  | "EXPENSE_CHANGED"
  | "EXPENSE_DELETED"
  /** G-167 (OD-283): the office assigned a web order to a shop / passed it to another. */
  | "SHOPIFY_ORDER_ASSIGNED"
  | "SHOPIFY_ORDER_PASSED";

/** How a catalog row came to exist — hand-work vs machine ingest. */
export type CatalogAuditSource =
  | "MANUAL"
  | "DRAFT_FINALIZE"
  | "INVOICE_INGEST"
  | "RECEIVING"
  | "KIOSK"
  /** G-129: the app created the item itself when a paper was read. */
  | "PAPER_AUTO";

/** One row of GET /catalog-audit-log (owner/manager). */
export interface CatalogAuditEntry {
  id: string;
  action: CatalogAuditAction;
  entityType: string;
  entityId: string;
  /** Display name at the time of the action — survives renames/deletions. */
  entityName: string;
  source: CatalogAuditSource;
  actor: { id: string; fullName: string; role: Role } | null;
  /** `{changes: {field: {from, to}}}` for updates; `{snapshot}` for creates. */
  metadata: unknown;
  createdAt: string;
  /** G-115: derived on read — who reversed this row, when and why. */
  undone: { by: string; at: string; reason: string } | null;
  /** G-115: on an ACTION_UNDONE row, the row it reversed. */
  reversedAuditLogId: string | null;
}

/** G-115: the outcome of undoing several catalog rows with one reason. */
export interface CatalogUndoManyResult {
  results: Array<{
    auditLogId: string;
    /** The row's entity name, so a refusal can be mapped back to its row. */
    entityName: string | null;
    ok: boolean;
    error?: string;
  }>;
  undone: number;
  failed: number;
}

export interface ListCatalogAuditLogQuery {
  actorUserId?: string;
  entityId?: string;
  action?: CatalogAuditAction;
  from?: string;
  to?: string;
  take?: number;
  skip?: number;
}

/** G-17: the recorded floor actions (Prisma `ShopAuditAction`). */
export type ShopAuditAction =
  | "SHOP_MODE_ENTERED"
  | "SHOP_MODE_EXITED"
  | "PIN_VERIFY_SUCCESS"
  | "PIN_VERIFY_FAILURE"
  | "PIN_ESCALATION_BLOCKED"
  | "SALE_CREATED"
  | "SALE_DISPATCHED"
  | "WASTE_LOGGED"
  | "STOCK_VIEWED"
  | "STOCK_ADJUSTED"
  | "SHOP_IDLE_LOCK"
  | "SHOP_BOUQUET_BUILT"
  | "TASK_CREATED"
  | "TASK_ASSIGNED"
  | "TASK_COMPLETED"
  | "TASK_CANCELLED"
  | "DELIVERY_LINE_CONFIRMED"
  | "SHOPIFY_ORDER_ACCEPTED"
  | "SHOPIFY_ORDER_PREPARED"
  | "SHOPIFY_ORDER_SOLD"
  | "SHOPIFY_ORDER_RELEASED"
  | "ORDER_PREPARATION_STARTED"
  | "ORDER_MARKED_READY"
  | "ORDER_BOUQUET_BUILT"
  | "ORDER_ITEM_PICKED"
  | "ORDER_ITEM_UNPICKED"
  | "SHIFT_CLOCKED_IN"
  | "SHIFT_STARTED_LATE"
  | "SHIFT_STARTED_EARLY"
  | "NO_SHIFT_DAY"
  | "SHIFT_CLOCKED_OUT"
  | "COURIER_ARRIVAL_CONFIRMED"
  | "TASK_SCHEDULE_CREATED"
  | "TASK_SCHEDULE_UPDATED"
  | "TASK_SCHEDULE_DELETED"
  | "BOUQUET_PHOTO_ATTACHED"
  | "ORDER_PHOTO_ATTACHED"
  | "SALE_BELOW_FLOOR"
  | "ACTION_UNDONE"
  | "ORDER_COLLECTED"
  | "ORDER_PICKUP_CONFIRMED"
  | "SHOP_ORDER_CREATED"
  | "ORDER_AMENDED_AT_SALE"
  | "ORDER_PAYMENT_TAKEN"
  | "ITEM_BARCODE_CAPTURED"
  | "ITEM_EDITED"
  | "ITEM_PHOTO_CHANGED"
  | "ITEM_LOOKED_UP"
  | "FOB_ISSUED"
  | "FOB_REVOKED"
  | "FOB_VERIFY_FAILURE"
  | "FOB_COUNTER_SUSPECT"
  | "BOX_ISSUED"
  | "BOX_RETIRED"
  | "BOX_EDITED"
  | "BOX_STOCK_MOVED_IN"
  | "BOX_STOCK_MOVED_OUT"
  | "BOX_PICK_ANSWERED"
  | "BOX_VIEWED"
  | "BOX_LABEL_PRINTED";

/** One row of GET /shop-mode/audit-log. */
/** G-18: where a kiosk action happened relative to the shop pin. UNKNOWN
 * (no usable coordinates) is itself a flag, not a pass. Null = pre-G-18 row. */
export type KioskGeoStatus = "OK" | "FAR" | "UNKNOWN";

/**
 * G-84 — one storage box as the CRM lists it. The floor plane has its own
 * copy of this shape in `shopMode.ts` because the two doors serve different
 * tokens; the fields are the same because the box is.
 */
export interface BoxSummary {
  id: string;
  code: string;
  /** What the label's barcode says, e.g. `BX000017`. */
  barcodeText: string;
  name: string | null;
  status: "ACTIVE" | "RETIRED";
  locationId: string;
  locationName: string;
  zoneId: string;
  keptInZone: { id: string; name: string } | null;
  issuedAt: string;
  issuedByName: string;
  retiredAt: string | null;
  itemCount: number;
  totalUnits: string;
}

/**
 * G-105 — one row of a PERSON's activity, from whichever stream recorded it.
 *
 * `action` is deliberately a plain string: it is a `ShopAuditAction`, a
 * `CatalogAuditAction` or an `AuthEvent` depending on `stream`, and the
 * rendering narrows it per stream rather than pretending one union fits all.
 */
export interface UserActivityEntry {
  id: string;
  stream: "floor" | "catalog" | "security";
  action: string;
  entityType: string | null;
  entityId: string | null;
  /** Catalog rows only — the item's name at the time. */
  entityName: string | null;
  /** Floor rows only; the other two streams happen at no shop. */
  location: { id: string; name: string; code: string } | null;
  createdAt: string;
}

export interface ShopAuditLogEntry {
  id: string;
  action: ShopAuditAction;
  createdAt: string;
  /** Null when the PIN'd person has since been deleted (SetNull). */
  actor: { id: string; fullName: string; role: Role } | null;
  /** Who armed the tablet for the session this action belongs to. */
  armedBy: { id: string; fullName: string };
  entityType: string | null;
  entityId: string | null;
  metadata: unknown;
  /** G-18 presence stamp — per action, not per session. */
  geoStatus: KioskGeoStatus | null;
  geoDistanceM: number | null;
  geoAccuracyM: number | null;
  geoLat: number | null;
  geoLng: number | null;
  /** How the SESSION was armed — a red arming taints every row inside it. */
  sessionGeoStatus: KioskGeoStatus | null;
  /** G-50: no scheduled shift covered this action — RECOMPUTED on read, so
   *  adding the missing shift afterwards clears it. Renders amber. */
  unscheduled: boolean;
  /** G-51: set when an owner/manager reversed this action's domain effect.
   *  The row itself is never erased — this drives the strikethrough. */
  undone: { by: string; at: string; reason: string } | null;
}

export interface ListShopAuditLogQuery {
  locationId?: string;
  actorUserId?: string;
  action?: ShopAuditAction;
  from?: string;
  to?: string;
  take?: number;
  skip?: number;
}

export interface VacationRequest {
  id: string;
  userId: string;
  /** Inclusive calendar-day range, ISO dates. */
  startDate: string;
  endDate: string;
  status: VacationRequestStatus;
  reason: string | null;
  decidedByUserId: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; fullName: string; role: Role };
  decidedBy: { id: string; fullName: string } | null;
}

export interface CreateVacationRequestPayload {
  startDate: string;
  endDate: string;
  reason?: string;
}

/** One row of GET /inventory/reorder-suggestions (Finalizing.MD T7/T8). */
/** G-43: one requirement a scheduled order cannot fully cover. */
export interface ForwardOrderShortfall {
  description: string;
  requiredQty: string;
  availableQty: string;
  incomingQty: string;
  transferableQty: string;
  missingQty: string;
}

export interface ForwardOrderView {
  orderId: string;
  code: string;
  scheduledDate: string;
  locationId: string;
  locationName: string;
  status: CustomerOrderStatus;
  customerName: string;
  verdict: "OK" | "SHORT" | "UNCHECKABLE";
  shortfall: ForwardOrderShortfall[];
  uncheckable: string[];
}

export interface ForwardPurchaseSuggestion {
  description: string;
  qty: string;
  locationId: string;
  locationName: string;
  neededBy: string;
  incomingCounted: Array<{
    supplierName: string;
    qty: string;
    arrivesAt: string;
  }>;
}

export interface ForwardTransferSuggestion {
  description: string;
  qty: string;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
  neededBy: string;
}

/** G-43: the two-week forward stock view — advisory, reserves nothing. */
export interface ForwardDemandView {
  horizonDays: number;
  from: string;
  to: string;
  orders: ForwardOrderView[];
  purchases: ForwardPurchaseSuggestion[];
  transfers: ForwardTransferSuggestion[];
}

export interface ReorderSuggestion {
  itemId: string;
  itemName: string;
  defaultUom: DefaultUom;
  isPerishable: boolean;
  category: { id: string; code: string; label: string };
  qtyOnHand: string;
  reorderThreshold: string;
  usedLast28Days: string;
  weeklyUsage: string;
  suggestedQty: string;
  lastSupplier: { id: string; name: string } | null;
  lastUnitCostEur: string | null;
  lastReceivedAt: string | null;
}

// ── Analytics (Finalizing.MD T4+T5) ──────────────────────────────────────────

export interface AnalyticsSalesSummary {
  saleCount: number;
  totalEur: string;
  totalCogsEur: string;
  grossProfitEur: string;
  totalCommissionEur: string;
  profitAfterCommissionEur: string;
  /** WO-B part 5 (OD-198): sales holding stock of unknown cost (COGS is their known part). */
  salesWithUnknownCostCount: number;
  /**
   * G-162: refunds given in the period — already subtracted from revenue,
   * COGS, gross profit, commission and IVA above (on the day given).
   */
  refunds: { count: number; totalEur: string };
  /**
   * G-29 — the delivery leg of the same period/location, aggregated from
   * DeliveryJob and keyed on `deliveredAt`. Never folded into `totalEur`:
   * OD-14 keeps `Sale.totalEur` goods-only for good.
   */
  delivery: {
    jobCount: number;
    /**
     * KI-146 (G-166): what customers were charged for delivery — a web
     * order's Shopify shipping, every other order's job fee. Gross.
     */
    shippingChargedEur: string;
    /** Shipping Shopify refunded in the period (subtracted in `marginEur`). */
    shippingRefundedEur: string;
    /** The couriers' payouts. */
    courierCostEur: string;
    /** shippingCharged − shippingRefunded − courierCost. */
    marginEur: string;
    /** Delivered but the client has not settled the fee yet. */
    uncollectedShippingEur: string;
    /** Delivered jobs with no payout snapshot — courier cost is understated. */
    jobsMissingPayoutCount: number;
  };
  /**
   * Goods margin − commission + delivery revenue − courier payout.
   * Still BEFORE VAT is removed from revenue — see `vat` for the deduction.
   */
  contributionEur: string;
  /**
   * G-30 — VAT derived from the per-line sale-time snapshots. `vatEur` is
   * the IVA inside goods prices; `contributionAfterVatEur` is
   * `contributionEur` with it removed. Every sale line carries a rate, so
   * both figures are complete rather than estimates. Delivery fees stay
   * gross — their VAT treatment is not modelled.
   */
  vat: {
    vatEur: string;
    contributionAfterVatEur: string;
  };
  /**
   * G-25 — revenue per BUSINESS day (Europe/Madrid), ascending. Lets a caller
   * average a TRADING day instead of a calendar day: days with no revenue are
   * simply absent, so excluding them from a divisor is trivial.
   */
  byDay: Array<{ date: string; totalEur: string; saleCount: number }>;
  bySeller: Array<{
    userId: string;
    fullName: string;
    role: string;
    saleCount: number;
    totalEur: string;
    commissionEur: string;
  }>;
}

export interface AnalyticsSpendSummary {
  /** Receives in the period — never a split-off part or a wholly undone receive (G-175). */
  batchCount: number;
  /** The priced receives' money only; see `unpricedReceiveCount`. */
  totalEur: string;
  /** G-175 (KI-153): receives whose cost was estimated or unknown — €0 in the total. */
  unpricedReceiveCount: number;
  byCategory: Array<{
    categoryId: string;
    code: string;
    label: string;
    totalEur: string;
    batchCount: number;
    unpricedCount: number;
  }>;
  bySupplier: Array<{
    supplierId: string | null;
    name: string;
    totalEur: string;
    batchCount: number;
    unpricedCount: number;
  }>;
}

/**
 * G-169 (WO-D): the home page's money in one answer (`GET /analytics/home`),
 * per shop and added once on the server. `scope` is what the by-shop card
 * shows (today, or a whole Barcelona month); `today` always today. Owner
 * only: the running costs and "Profit today" — null with no cost on record
 * (KI-115), never €0.00.
 */
export interface AnalyticsHomeShopRow {
  locationId: string;
  locationName: string;
  saleCount: number;
  /** Gross, goods only. */
  totalEur: string;
  /** The contribution after VAT (G-45); running costs not taken off per shop. */
  contributionAfterVatEur: string;
  salesWithUnknownCostCount: number;
  /** Revenue ÷ tickets; null with no ticket. */
  averageSaleEur: string | null;
  /** This shop's share of the period's revenue, 1 dp. */
  revenueSharePercent: string;
  wasteCostEur: string;
  wasteUnknownCostEntryCount: number;
  /** The shop's daily nut this month (recurring costs only); null for a manager. */
  dailyFixedCostEur: string | null;
  /**
   * G-204: this shop's running costs for the block's period (today: its daily
   * nut plus today's one-offs; a month: its bills active in it plus its
   * one-offs), and the profit left after them. Owner only; null with no cost
   * on record for the shop (KI-115), never €0.00.
   */
  runningCostsEur: string | null;
  profitEur: string | null;
}

export interface AnalyticsHomeBlock {
  byLocation: AnalyticsHomeShopRow[];
  totals: {
    saleCount: number;
    totalEur: string;
    contributionAfterVatEur: string;
    salesWithUnknownCostCount: number;
    wasteCostEur: string;
    wasteUnknownCostEntryCount: number;
    /** G-204: the rows' known running costs added once; null when no shop has any on record. */
    runningCostsEur: string | null;
    /**
     * G-204: the sum of the rows' profit — contribution after VAT less the
     * running costs; null while no shop has a cost on record, and null when a
     * shop that sold something has none (a sum with an unknown is unknown).
     */
    profitEur: string | null;
  };
}

export interface AnalyticsHomeSummary {
  /** Today in Barcelona, YYYY-MM-DD. */
  businessDate: string;
  period:
    | { kind: "today"; from: string; to: string }
    | { kind: "month"; year: number; month: number; from: string; to: string };
  scope: AnalyticsHomeBlock;
  today: AnalyticsHomeBlock & {
    /** G-24 Today's team: each seller's tickets and commission today. */
    bySeller: Array<{ userId: string; totalEur: string; commissionEur: string }>;
    faultWasteByActor: Array<{ userId: string; costEur: string }>;
  };
  /** G-25: this month's average trading day (today left out), and today against it. */
  baseline: {
    month: string;
    tradingDays: number;
    averageEur: string | null;
    revenueDeltaPercent: number | null;
  };
}

export interface AnalyticsWasteSummary {
  entryCount: number;
  totalQty: string;
  /** WO-B part 4: the known part only — see the two counts below. */
  totalCostEur: string;
  unknownCostQty: string;
  unknownCostEntryCount: number;
  byCause: Array<{
    cause: string;
    qty: string;
    costEur: string;
    unknownCostQty: string;
    count: number;
  }>;
  byActor: Array<{
    userId: string;
    fullName: string;
    qty: string;
    costEur: string;
    unknownCostQty: string;
    count: number;
  }>;
  /**
   * G-24 — the fault-only slice of `byActor` (BROKEN_DURING_PREP +
   * STAFF_ACCIDENT). `byCause` and `byActor` are separate groupings, so this
   * is the only way to answer "how much did this person break".
   */
  faultByActor: Array<{
    userId: string;
    fullName: string;
    qty: string;
    costEur: string;
    count: number;
  }>;
  recent: Array<{
    id: string;
    itemName: string;
    quantity: string;
    uom: DefaultUom;
    cause: string;
    reason: string;
    loggedBy: string;
    occurredAt: string;
    costEur: string | null;
  }>;
}

export interface AnalyticsStockValue {
  /** WO-B part 4: lots of unknown cost left out — see the two counts below. */
  totalEur: string;
  unknownCostQty: string;
  unknownCostBatchCount: number;
  itemCount: number;
  batchCount: number;
  byCategory: Array<{
    categoryId: string;
    code: string;
    label: string;
    valueEur: string;
    unknownCostQty: string;
    itemCount: number;
  }>;
}

// ── Supplier honesty extensions (Finalizing.MD T6) ───────────────────────────

export interface SupplierRankingRow {
  supplierId: string;
  code: string;
  name: string;
  batchCount: number;
  invoicedQty: string;
  receivingWasteQty: string;
  /** KI-93: units that arrived bad — receiving waste or damage noted on delivery lines, lot by lot. */
  badQty: string;
  /** Out-of-box-bad percentage over the range (badQty ÷ invoicedQty); null when nothing invoiced. */
  badPct: number | null;
  totalSpendEur: string;
}

export interface SupplierBatchQualityRow {
  batchId: string;
  batchCode: string;
  receivedAt: string;
  itemId: string;
  itemName: string;
  invoicedQty: string;
  usableQty: string;
  receivingWasteQty: string;
  /** KI-93: units noted damaged on the delivery line (still stock, OD-125). */
  damagedOnArrivalQty: string;
  /** KI-93: units that arrived bad — the larger of receivingWasteQty and damagedOnArrivalQty. */
  badQty: string;
  badPct: number | null;
  invoiceTotalEur: string;
  unitCostEur: string;
  quotedUnitPriceEur: string | null;
}

export interface ItemPriceHistory {
  itemId: string;
  itemName: string;
  points: Array<{
    batchId: string;
    receivedAt: string;
    supplierId: string | null;
    supplierName: string | null;
    invoicedQty: string;
    quotedUnitPriceEur: string | null;
    unitCostEur: string;
  }>;
}

// ── G-32 recurring operating costs + G-39 one-time expenses ──────────────────
// One contiguous block, appended last, so it merges cleanly alongside other
// in-flight work on this file.

export type RecurringCostCategory =
  | "RENT"
  | "UTILITIES"
  | "CONNECTIVITY"
  | "INSURANCE"
  | "SOFTWARE"
  | "OTHER";

export type OneTimeExpenseCategory =
  | "REPAIR"
  | "EQUIPMENT"
  | "SUPPLIES"
  | "MARKETING"
  | "PROFESSIONAL_FEES"
  | "LICENCE_FEES"
  | "OTHER";

export type OperatingCostLocationScope =
  | "manager_shop"
  | "single_shop"
  | "all_shops";

export interface RecurringCostRecord {
  id: string;
  locationId: string;
  location: { id: string; name: string; code: string };
  name: string;
  monthlyAmountEur: string;
  category: RecurringCostCategory;
  /** `YYYY-MM-DD` — first month this amount applies to. */
  activeFrom: string;
  /** `YYYY-MM-DD` inclusive, or null while the cost is still running. */
  activeTo: string | null;
  createdBy: { id: string; fullName: string };
  createdAt: string;
  updatedAt: string;
}

export interface RecurringCostListResponse {
  locationScope: OperatingCostLocationScope;
  locationId: string | null;
  rows: RecurringCostRecord[];
}

export interface CreateRecurringCostPayload {
  locationId: string;
  name: string;
  monthlyAmountEur: number;
  category: RecurringCostCategory;
  activeFrom: string;
  activeTo?: string;
}

export interface UpdateRecurringCostPayload {
  locationId?: string;
  name?: string;
  monthlyAmountEur?: number;
  category?: RecurringCostCategory;
  activeFrom?: string;
  /** null re-opens a closed cost. */
  activeTo?: string | null;
}

export interface OneTimeExpenseRecord {
  id: string;
  locationId: string;
  location: { id: string; name: string; code: string };
  /** `YYYY-MM-DD` — the day the money was spent, which decides its month. */
  incurredOn: string;
  amountEur: string;
  description: string;
  notes: string | null;
  category: OneTimeExpenseCategory;
  /** False only for expenses older than G-135, which made the receipt photo required. */
  hasReceipt: boolean;
  /** G-135: the optional invoice PDF is on file. */
  hasInvoice: boolean;
  /** OD-177: the owner (or an admin) said no invoice will come — who, when, why. */
  noInvoice: { at: string; by: { id: string; fullName: string }; reason: string } | null;
  createdBy: { id: string; fullName: string };
  createdAt: string;
  updatedAt: string;
}

export interface OneTimeExpenseListResponse {
  locationScope: OperatingCostLocationScope;
  locationId: string | null;
  totalEur: string;
  rows: OneTimeExpenseRecord[];
}

export interface CreateOneTimeExpensePayload {
  locationId: string;
  incurredOn: string;
  amountEur: number;
  description: string;
  notes?: string;
  category: OneTimeExpenseCategory;
}

export interface UpdateOneTimeExpensePayload {
  locationId?: string;
  incurredOn?: string;
  amountEur?: number;
  description?: string;
  notes?: string | null;
  category?: OneTimeExpenseCategory;
}

export interface OperatingCostsSummary {
  periodYear: number;
  periodMonth: number;
  locationScope: OperatingCostLocationScope;
  locationId: string | null;
  tradingDaysInMonth: number;
  tradingDaysBasis: "CALENDAR_DAYS";
  tradingDaysNote: string;
  monthlyFixedCostEur: string;
  /** The daily nut — recurring costs only (OD-16 recommendation). */
  dailyFixedCostEur: string;
  /** One-off spend in the month; deliberately NOT inside the daily nut. */
  oneTimeTotalEur: string;
  oneTimeTreatment: "CHARGED_TO_ITS_DAY" | "SPREAD_ACROSS_MONTH";
  today: {
    businessDate: string;
    isInPeriod: boolean;
    fixedCostEur: string;
    oneTimeCostEur: string;
    totalCostEur: string;
  };
  byLocation: Array<{
    locationId: string;
    locationName: string;
    locationCode: string;
    monthlyFixedCostEur: string;
    dailyFixedCostEur: string;
    oneTimeTotalEur: string;
    recurringCostCount: number;
    oneTimeExpenseCount: number;
    /** G-204: this shop's share of today's running costs (daily nut + today's one-offs). */
    todayFixedCostEur: string;
    todayOneTimeCostEur: string;
    todayTotalCostEur: string;
  }>;
  byCategory: Array<{
    category: RecurringCostCategory;
    monthlyAmountEur: string;
  }>;
}

/** G-6: the courier's own earnings view. */
export interface CourierEarnings {
  jobs: Array<{
    id: string;
    code: string;
    deliveredAt: string | null;
    deliveryAddress: string;
    payoutEur: string | null;
    paid: boolean;
    paidAt: string | null;
  }>;
  totals: {
    jobCount: number;
    totalEur: string;
    paidEur: string;
    unpaidEur: string;
  };
}

/** G-6: one courier's unpaid delivered jobs, for the settlement table. */
export interface SettlementGroup {
  courier: { id: string; fullName: string };
  jobs: Array<{
    id: string;
    code: string;
    deliveredAt: string | null;
    deliveryAddress: string;
    payoutEur: string | null;
  }>;
  totalEur: string;
  jobCount: number;
}

/** G-45 part 3: one sale line that went below its snapshotted hard floor. */
export interface BelowFloorFlag {
  saleLineId: string;
  kind: "BOUQUET" | "ITEM";
  description: string;
  bouquetBuildId: string | null;
  priceEur: string;
  floorEur: string;
  shortfallEur: string;
}

/** G-45 part 3: an unjustified below-floor line, for the owner's list. */
export interface BelowFloorEntry extends BelowFloorFlag {
  saleId: string;
  soldAt: string;
  location: { id: string; name: string };
  soldBy: { id: string; fullName: string };
}

/** G-149: one piece of an AI system's instructions on the AI control page. */
export interface AiInstructionPart {
  /** The contract the code depends on: shown, never editable. */
  locked: boolean;
  /** An editable part's name in stored versions (G-149 S2); null where the page cannot edit it. */
  key: string | null;
  /** "Who it is", "system.md", "flowershop-brand / SKILL.md", "Answer shape" — or null. */
  title: string | null;
  text: string;
  /** Blog writer skills: these files are the ones uploaded to the platform. */
  uploaded: boolean | null;
}

export type AiControlSystemKey = "ITEM_PHOTO_SEARCH" | "BLOG_POST_WRITE" | "TELEGRAM_ASSISTANT";

export interface AiControlSystem {
  key: AiControlSystemKey;
  models: string[];
  version: string | null;
  state: "LIVE" | "NOT_SET_UP" | "NOT_CONNECTED";
  parts: AiInstructionPart[];
  /** Where editing stands, for the systems the page can edit (S2); null elsewhere. */
  editing: AiEditingState | null;
}

/** G-149 S2: the systems whose editable parts the page can change. */
export type AiEditableSystemKey = "ITEM_PHOTO_SEARCH";

export interface AiEditingState {
  codeVersion: string;
  live: { number: number; version: string; since: string | null; by: string | null; note: string | null };
  /** `{ partKey: text }` as live now. */
  liveTexts: Record<string, string>;
  draft: {
    texts: Record<string, string>;
    note: string | null;
    savedAt: string | null;
    savedBy: string | null;
    /** Tested on past searches as it stands now. */
    tested: boolean;
    changedKeys: string[];
  } | null;
  /** Past kiosk searches exist to test on; when none do, publishing needs no test. */
  canTest: boolean;
}

export interface AiInstructionVersionRow {
  number: number;
  version: string;
  /** Null for the text in code (version 0). */
  note: string | null;
  codeVersion: string;
  createdAt: string | null;
  createdBy: string | null;
  texts: Record<string, string>;
  live: boolean;
  calls: number;
  costEur: string;
}

export interface AiInstructionVersions {
  liveNumber: number;
  versions: AiInstructionVersionRow[];
}

/** A past kiosk search a draft can be tested on: its pick is in stock at that shop today. */
export interface AiTestablePastSearch {
  id: string;
  createdAt: string;
  locationName: string;
  chosenItem: { id: string; name: string };
  chosenRank: number | null;
}

export interface AiPhotoSearchTrialSide {
  version: string;
  isFlower: boolean;
  flowerType: string | null;
  colour: string | null;
  confidence: "HIGH" | "MEDIUM" | "LOW";
  candidates: FloorPhotoSearchCandidate[];
  /** Where the florist's pick lands, 1-based; null = not offered. */
  pickRank: number | null;
  costEur: string | null;
}

export interface AiPhotoSearchTrial {
  searchId: string;
  chosenItemId: string;
  live: AiPhotoSearchTrialSide;
  draft: AiPhotoSearchTrialSide;
}

/** G-171: net (before IVA), the IVA, and gross (IVA inside) — the server rounds once. */
export interface IvaMoney {
  netEur: string;
  ivaEur: string;
  grossEur: string;
}

/** G-171: a calendar quarter, as far as it has happened (modelo 303 is quarterly). */
export interface IvaQuarterRef {
  year: number;
  /** 1–4. */
  quarter: number;
  /** The months counted (1–12): the quarter's months up to the current Barcelona month. */
  months: number[];
  /** True while the quarter runs. */
  toDate: boolean;
}

/** G-171: the Finances "IVA on sales" card for one month (owner; a manager for their shop). */
export interface SalesIvaSummary {
  year: number;
  month: number;
  locationId: string | null;
  /** The sales' IVA less the refunds' — what the month owes on sales. */
  collected: IvaMoney;
  /** Before refunds; `count` = tickets. */
  sales: IvaMoney & { count: number; lineCount: number };
  /** What the month's refund events took off (negative when removals gave more back). */
  refunds: IvaMoney & { count: number };
  byRate: Array<{ ratePercent: string } & IvaMoney>;
  /** One row per category and rate. */
  byCategory: Array<{ categoryId: string; label: string; ratePercent: string } & IvaMoney>;
  /** Empty when one shop was asked for. */
  byShop: Array<{ locationId: string; name: string; code: string } & IvaMoney>;
  /** The quarter's IVA collected so far (its months added); null when none of its months has begun. */
  quarter: (IvaQuarterRef & { collected: IvaMoney }) | null;
}

/** G-171: one period's balance with Hacienda — a month, or a quarter (its months added). */
export interface HaciendaBalanceFigures {
  collectedEur: string;
  /** Deductible: IVA printed on counted facturas, less counted credit notes (G-173). */
  deductible: { totalEur: string; facturaCount: number; creditNoteCount: number };
  /** collected − deductible: above zero owed, below zero to recover. */
  balanceEur: string;
  /** IVA paid with no factura (OD-164): shown, never subtracted. */
  notDeductible: {
    totalEur: string;
    typedNoDeliveryEur: string;
    typedNoDeliveryCount: number;
    noFacturaEur: string;
    noFacturaCount: number;
  };
  /** Not subtracted yet: counts when the factura comes. */
  waitingForFactura: { ivaEur: string; count: number };
  notCounted: {
    deliveryFees: { feesEur: string; count: number };
    runningCosts: { amountEur: string; count: number };
  };
}

/** G-171: the Finances "Balance with Hacienda" card (owner only): the month and its quarter. */
export interface HaciendaBalance {
  year: number;
  month: number;
  locationId: string | null;
  thisMonth: HaciendaBalanceFigures;
  quarter: (IvaQuarterRef & HaciendaBalanceFigures) | null;
}
